// INDEX_SPLIT_B4_v1 (07.09.2026) -- Super-Admin: AdminSubscriptionsPage, ActiveSalonsWidget, ServerStatsWidget, AdminAiVisibilityMini, AdminAiCitationsMini, AdminDashboardPage, AdminMonitorPage, AdminPricingPage, AdminCostsPage, AdminSalonsPage, AdminFrontendErrorsPage, AdminAuditPage, AdminOnboardingPage, AdminUpdatesPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { Activity, Bar, BarChart, Bot, Building2, CartesianGrid, Check, CheckCircle, CreditCard, Euro, Eye, Mail, MessageSquare, PhoneCall, Plus, RefreshCw, ResponsiveContainer, Send, Server, Settings, Tooltip, Trash2, TrendingUp, Users, XAxis, XCircle, YAxis, Zap, useEffect, useState } from "../core/vendor.js";
import { RetellSetupCard } from "../ai/ai.jsx";
import { AdminKasseBox } from "../finance/kasse.jsx"; // KASSE_ABO_v1
import { api } from "../core/api.js";
import { COLORS, NavigateContext, formatDate, useIsMobile } from "../core/config.jsx";
import { Badge, Btn, Card, DateDMYInput, Modal, Skeleton, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";

// TERMS_REGISTER_v1 -- Nachweis der AGB/AVV/Datenschutz-Zustimmungen je Salon (dauerhaftes Register terms_acceptances)
const TermsAcceptancesCard = ({ salonId }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    setData(null); setErr("");
    fetch(`/api/admin/terms-acceptances?salon_id=${salonId}`, { headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") } })
      .then(r => r.json()).then(j => { if (alive) { if (j && j.acceptances) setData(j); else setErr((j && j.error) || "Laden fehlgeschlagen"); } })
      .catch(() => alive && setErr("Laden fehlgeschlagen"));
    return () => { alive = false; };
  }, [salonId]);
  const downloadCsv = async () => {
    const r = await fetch(`/api/admin/terms-acceptances?salon_id=${salonId}&format=csv`, { headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `agb-zustimmungen-salon-${salonId}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
  };
  const fmt = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch (e) { return iso; } };
  return (
    <div style={{ marginTop: 6, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, flex: 1 }}>AGB-/AVV-Zustimmungen (Nachweis)</div>
        {data && data.acceptances.length > 0 && <Btn size="sm" variant="ghost" onClick={downloadCsv}>CSV</Btn>}
      </div>
      {err && <div style={{ fontSize: 12, color: COLORS.danger }}>{err}</div>}
      {!data && !err && <div style={{ fontSize: 12, color: COLORS.textLight }}>Lade …</div>}
      {data && (
        <>
          <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 8 }}>
            Aktuelle Fassung: <b>{data.current_version}</b>. Jede Bestätigung wird dauerhaft mit Zeitpunkt, IP, Browser und Prüfsumme des Wortlauts gespeichert (nicht änderbar).
          </div>
          {data.pending.length > 0 && (
            <div style={{ fontSize: 11.5, color: "#92400E", background: "#FEF3C7", borderRadius: 6, padding: "6px 8px", marginBottom: 8 }}>
              Noch nicht bestätigt: {data.pending.map(p => `${p.name || p.email} (${p.role})`).join(", ")}
            </div>
          )}
          {data.acceptances.length === 0 ? <div style={{ fontSize: 12, color: COLORS.textLight }}>Noch keine Zustimmung gespeichert.</div> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                <thead><tr style={{ textAlign: "left", color: COLORS.textLight }}>
                  <th style={{ padding: "4px 6px" }}>Zeitpunkt</th><th style={{ padding: "4px 6px" }}>Person</th><th style={{ padding: "4px 6px" }}>Rolle</th><th style={{ padding: "4px 6px" }}>Version</th><th style={{ padding: "4px 6px" }}>IP</th>
                </tr></thead>
                <tbody>
                  {data.acceptances.map(a => (
                    <tr key={a.id} style={{ borderTop: `1px solid ${COLORS.border}` }} title={`${a.user_agent || ""}${a.content_sha256 ? "\nSHA-256: " + a.content_sha256 : ""}${a.source !== "click_wrap" ? "\nQuelle: " + a.source : ""}`}>
                      <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>{fmt(a.accepted_at)}</td>
                      <td style={{ padding: "4px 6px" }}>{a.user_name || "—"}<div style={{ color: COLORS.textLight }}>{a.user_email}</div></td>
                      <td style={{ padding: "4px 6px" }}>{a.role}</td>
                      <td style={{ padding: "4px 6px", whiteSpace: "nowrap" }}>{a.version}{a.source !== "click_wrap" ? " *" : ""}</td>
                      <td style={{ padding: "4px 6px" }}>{a.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.acceptances.some(a => a.source !== "click_wrap") && <div style={{ fontSize: 10.5, color: COLORS.textLight, marginTop: 4 }}>* aus dem Audit-Log übernommen (vor dem 14.09.2026, IP damals nicht korrekt erfasst)</div>}
            </div>
          )}
        </>
      )}
    </div>
  );
};


// ============================================================
// PROFILE PAGE
// ============================================================

export const AdminSubscriptionsPage = ({ currentUser }) => {
      const [subs, setSubs] = React.useState([]);
      const [loading, setLoading] = React.useState(true);
      const [error, setError] = React.useState(null);

      React.useEffect(() => {
        fetch("/api/admin/subscriptions", {
          headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") }
        })
        .then(r => r.json())
        .then(data => {
          if (data.error) setError(data.error);
          else setSubs(data.subscriptions || []);
          setLoading(false);
        })
        .catch(() => { setError("Verbindungsfehler"); setLoading(false); });
      }, []);

      const formatDate = (ts) => { if (!ts) return "-"; const d = new Date(ts * 1000); return d.toLocaleDateString("de-DE"); };
      const statusColors = { active: "#16a34a", canceled: "#dc2626", past_due: "#f59e0b", trialing: "#6366f1", incomplete: "#9ca3af" };
      const statusLabels = { active: "Aktiv", canceled: "Gekündigt", past_due: "Zahlung fällig", trialing: "Testphase", incomplete: "Unvollständig" };

      // HEADER_ALL_PAGES_v1 (M2): Standard-Header mit Menü-Taste in allen drei Zuständen
      if (loading) return React.createElement("div", null,
        React.createElement(Header, {title: "Abonnements"}),
        React.createElement("div", {style:{padding:"40px",textAlign:"center"}}, "Lade Abonnements...")
      );
      if (error) return React.createElement("div", null,
        React.createElement(Header, {title: "Abonnements"}),
        React.createElement("div", {style:{padding:"40px",textAlign:"center",color:"red"}}, "Fehler: " + error)
      );

      return React.createElement("div", null,
        React.createElement(Header, {title: "Abonnements", subtitle: subs.length + " Abonnements insgesamt"}),

        subs.length === 0
          ? React.createElement("div", {style:{textAlign:"center",padding:"60px",background:"var(--card)",borderRadius:"12px",color:"#6b7280"}}, "Noch keine Abonnements vorhanden.")
          : React.createElement("div", {style:{background:"var(--card)",borderRadius:"12px",boxShadow:"0 2px 8px rgba(0,0,0,0.08)",overflow:"hidden"}},
              React.createElement("table", {style:{width:"100%",borderCollapse:"collapse",fontSize:"14px"}},
                React.createElement("thead", null,
                  React.createElement("tr", {style:{background:"var(--cardHover)",borderBottom:"2px solid #e5e7eb"}},
                    /* SAAS_ADMIN_v1 -- Kundennummer-Spalte vorne */
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600",fontSize:"12px",color:"#6b7280"}}, "Kd-Nr."),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600"}}, "Kunde"),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600"}}, "E-Mail"),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600"}}, "Plan"),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600"}}, "Status"),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600"}}, "Seit"),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600"}}, "Nächste Abrechnung"),
                    /* SAAS_ADMIN_v1 -- Lexware + Stripe Spalten hinten */
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600",fontSize:"12px"}}, "Lexware"),
                    React.createElement("th", {style:{padding:"12px 16px",textAlign:"left",fontWeight:"600",fontSize:"12px"}}, "Stripe")
                  )
                ),
                React.createElement("tbody", null,
                  subs.map((sub) =>
                    React.createElement("tr", {key: sub.id, style:{borderBottom:"1px solid var(--border)"}},
                      /* SAAS_ADMIN_v1 -- Kd-Nr modern formatiert: #008 etc. */
                      React.createElement("td", {style:{padding:"12px 16px",fontVariantNumeric:"tabular-nums",fontFamily:"ui-monospace, monospace",fontSize:"13px",color:"#6b7280"}},
                        sub.salon_id ? "#" + String(sub.salon_id).padStart(3, "0") : "-"
                      ),
                      React.createElement("td", {style:{padding:"12px 16px",fontWeight:"500"}},
                        sub.salon_name || sub.customer_name || "-"
                      ),
                      React.createElement("td", {style:{padding:"12px 16px"}}, sub.customer_email || "-"),
                      /* INVOICE_SUBS_v1 -- Rechnungs-Kunden haben keinen Stripe-Betrag: Planname + "Rechnung" zeigen */
                      React.createElement("td", {style:{padding:"12px 16px",fontWeight:"600"}},
                        sub.source === "invoice"
                          ? ((sub.plan_name ? sub.plan_name.charAt(0).toUpperCase() + sub.plan_name.slice(1) : "Plan") + " · Rechnung")
                          : (sub.plan_amount + " EUR/" + (sub.plan_interval === "year" ? "Jahr" : "Monat"))),
                      React.createElement("td", {style:{padding:"12px 16px"}},
                        React.createElement("span", {style:{background:(statusColors[sub.status]||"#9ca3af")+"20",color:statusColors[sub.status]||"#9ca3af",padding:"4px 10px",borderRadius:"6px",fontSize:"12px",fontWeight:"600"}}, statusLabels[sub.status] || sub.status),
                        sub.cancel_at_period_end ? React.createElement("span", {style:{marginLeft:"8px",fontSize:"11px",color:"#dc2626"}}, "(kündigt)") : null
                      ),
                      React.createElement("td", {style:{padding:"12px 16px"}}, formatDate(sub.created)),
                      React.createElement("td", {style:{padding:"12px 16px"}}, formatDate(sub.current_period_end)),
                      /* SAAS_ADMIN_v1 -- Lexware-ID inline-editierbar via prompt */
                      React.createElement("td", {style:{padding:"12px 16px",fontSize:"13px"}},
                        sub.salon_id
                          ? React.createElement("button", {
                              onClick: async () => {
                                const cur = sub.lexware_customer_id || "";
                                const next = window.prompt("Lexware-Kundennummer für " + (sub.salon_name || sub.customer_name) + ":\n(leer lassen zum Entfernen)", cur);
                                if (next === null) return;
                                const res = await api.request("/admin/salons/" + sub.salon_id + "/lexware", {
                                  method: "PUT",
                                  body: JSON.stringify({ lexware_customer_id: next.trim() })
                                });
                                if (res && res.ok) location.reload();
                                else alert("Speichern fehlgeschlagen");
                              },
                              style: {
                                background: sub.lexware_customer_id ? "rgba(124,58,237,0.08)" : "transparent",
                                border: "1px dashed " + (sub.lexware_customer_id ? "rgba(124,58,237,0.4)" : "var(--border)"),
                                borderRadius: "6px",
                                padding: "4px 10px",
                                cursor: "pointer",
                                color: sub.lexware_customer_id ? "var(--text)" : "#9ca3af",
                                fontSize: "12px",
                                fontFamily: sub.lexware_customer_id ? "ui-monospace, monospace" : "inherit"
                              }
                            }, sub.lexware_customer_id || "+ eintragen")
                          : "-"
                      ),
                      /* SAAS_ADMIN_v1 -- Stripe-Direktlink */
                      React.createElement("td", {style:{padding:"12px 16px",fontSize:"13px"}},
                        sub.stripe_customer_id
                          ? React.createElement("a", {
                              href: "https://dashboard.stripe.com/customers/" + sub.stripe_customer_id,
                              target: "_blank",
                              rel: "noopener",
                              style: { color: "#7C3AED", textDecoration: "none", fontWeight: 600 }
                            }, "Stripe →")
                          /* INVOICE_SUBS_v1 -- manuell fakturierte Kunden als "Rechnung" kennzeichnen */
                          : (sub.source === "invoice"
                              ? React.createElement("span", {style:{color:"#9ca3af",fontSize:"12px",fontWeight:600}}, "Rechnung")
                              : "-")
                      )
                    )
                  )
                )
              )
            )
      );
    };

// ============================================================
// ACTIVE_SESSIONS_v1 + SYSTEM_STATS_v1 + MAINTENANCE_v1 -- Admin-Widgets
// ============================================================

// Widget: welche Salons sind gerade online?
export const ActiveSalonsWidget = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(() => {
    api.request("/admin/active-sessions?max_age_min=15").then(r => {
      if (r.ok) setData(r.data);
      setLoading(false);
    });
  }, []);
  React.useEffect(() => {
    load();
    const t = setInterval(load, 30000);  // refresh alle 30s
    return () => clearInterval(t);
  }, [load]);

  if (loading) return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <Skeleton width={180} height={18} />
        <Skeleton width={120} height={12} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 8 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8 }}>
            <Skeleton width={8} height={8} style={{ borderRadius: "50%", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <Skeleton width="60%" height={13} />
              <div style={{ height: 4 }} />
              <Skeleton width="40%" height={11} />
            </div>
            <Skeleton width={50} height={11} />
          </div>
        ))}
      </div>
    </Card>
  );
  if (!data) return null;

  const online = data.items.filter(s => s.online);
  const offline = data.items.filter(s => !s.online);

  const fmtAge = (m) => {
    if (m == null) return "noch nie";
    if (m < 1) return "gerade jetzt";
    if (m < 60) return m + " Min";
    if (m < 1440) return Math.floor(m / 60) + " h";
    return Math.floor(m / 1440) + " Tagen";
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <span>🟢 Live-Salons</span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: "rgba(16,185,129,0.18)", color: "#10B981" }}>{online.length} aktiv</span>
        </h3>
        <span style={{ fontSize: 11, color: COLORS.textLight }}>aktiv = letzter Zugriff &lt; 15 Min</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 8 }}>
        {data.items.map(s => (
          <div key={s.salon_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "var(--cardHover)", borderRadius: 8, borderLeft: `3px solid ${s.online ? "#10B981" : (s.age_minutes != null && s.age_minutes < 1440 ? "#9CA3AF" : "#374151")}` }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.online ? "#10B981" : "#9CA3AF", boxShadow: s.online ? "0 0 6px #10B981" : "none", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.salon_name}</div>
              <div style={{ fontSize: 11, color: "var(--textLight)" }}>{s.city || "—"} · {s.plan || "—"}</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0, fontSize: 11 }}>
              <div style={{ fontWeight: 600, color: s.online ? "#10B981" : "var(--textLight)" }}>{s.online ? "online" : fmtAge(s.age_minutes)}</div>
              <div style={{ color: "var(--textLight)" }}>{s.active_users || 0}/{s.total_users} User</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// Widget: Server-Auslastung (CPU/RAM/Latenz/DB-Pool) + Maintenance-Toggle
export const ServerStatsWidget = () => {
  const [data, setData] = React.useState(null);
  const [maintLoading, setMaintLoading] = React.useState(false);

  const load = React.useCallback(() => {
    api.request("/admin/system-stats").then(r => {
      if (r.ok) setData(r.data);
    });
  }, []);
  React.useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const toggleMaintenance = async () => {
    if (!data) return;
    const next = !data.maintenance_mode;
    if (next && !confirm("Wartungsmodus EIN schalten? Alle Salons sehen einen Banner und der Logger schweigt während du updatest.")) return;
    setMaintLoading(true);
    const r = await api.request("/admin/maintenance", { method: "POST", body: JSON.stringify({ enable: next }) });
    setMaintLoading(false);
    if (r.ok) load();
  };

  if (!data) return null;

  const Tacho = ({ label, value, unit, max = 100, suffix }) => {
    const pct = value == null ? 0 : Math.min(100, (value / max) * 100);
    let color = "#10B981";
    if (pct >= 80) color = "#EF4444";
    else if (pct >= 50) color = "#F59E0B";
    return (
      <div style={{ flex: 1, padding: 12, background: "var(--cardHover)", borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
        <div style={{ fontSize: 11, color: "var(--textLight)", marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: color }}>{value == null ? "–" : value}<span style={{ fontSize: 14, color: "var(--textLight)", fontWeight: 400 }}>{unit}</span></div>
        {suffix && <div style={{ fontSize: 10, color: "var(--textLight)", marginTop: 2 }}>{suffix}</div>}
        <div style={{ height: 4, background: "var(--border)", borderRadius: 2, marginTop: 6, overflow: "hidden" }}>
          <div style={{ height: "100%", width: pct + "%", background: color, transition: "width 0.4s, background 0.4s" }} />
        </div>
      </div>
    );
  };

  return (
    <Card style={{ marginBottom: 16, border: data.maintenance_mode ? `2px solid #F59E0B` : `1px solid ${COLORS.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
          <span>⚙️ Server-Auslastung</span>
          {data.maintenance_mode && <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: "rgba(245,158,11,0.2)", color: "#F59E0B" }}>WARTUNGSMODUS AN</span>}
        </h3>
        <Btn size="sm" variant={data.maintenance_mode ? "danger" : "secondary"} onClick={toggleMaintenance} disabled={maintLoading}>
          🔧 {maintLoading ? "…" : (data.maintenance_mode ? "Wartung beenden" : "Wartung starten")}
        </Btn>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr 1fr" : "repeat(4, 1fr)", gap: 8 }}>
        <Tacho label="CPU (1 Min)" value={data.cpu?.load_1m_pct} unit="%" suffix={`${data.cpu?.cores || 0} Core(s)`} />
        <Tacho label="RAM" value={data.ram?.pct} unit="%" suffix={`${data.ram?.used_mb || 0} / ${data.ram?.total_mb || 0} MB`} />
        <Tacho label="API-Latenz" value={data.api_latency?.avg_ms} unit=" ms" max={500} suffix={`p95: ${data.api_latency?.p95_ms || "–"} ms (${data.api_latency?.samples || 0} samples)`} />
        <Tacho label="Disk" value={data.disk?.pct} unit="%" suffix={`${data.disk?.free_gb || 0} GB frei`} />
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: "var(--textLight)" }}>
        Tornado-Worker: {data.shearly_process?.memory_mb || 0} MB · DB-Pool: {data.db_pool?.active != null ? `${data.db_pool.active} aktiv / ${data.db_pool.idle} frei` : "–"} · Auto-Refresh alle 10s
      </div>
    </Card>
  );
};


// ============================================================
// PAGE: Admin Dashboard (Super-Admin only)
// ============================================================
// AI_VISIBILITY_v1 -- Admin-Mini-Widget: Status aller Salons + Hilfe
export const AdminAiVisibilityMini = () => {
  const navigate = React.useContext(NavigateContext);
  const [items, setItems] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    api.request("/admin/ai-visibility/overview").then(r => {
      if (r && r.ok) setItems(r.data?.items || []);
    }).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  const total = items.length;
  const enabled = items.filter(i => i.enabled).length;
  const withScore = items.filter(i => i.enabled && typeof i.score === "number");
  const avgScore = withScore.length > 0
    ? (withScore.reduce((s, i) => s + i.score, 0) / withScore.length).toFixed(1)
    : "—";
  const needSnippet = items.filter(i => i.enabled && i.score !== null && i.score < 5).length;
  const fullyLive = items.filter(i => i.enabled && i.score === 5).length;
  const noWebsite = items.filter(i => i.enabled && !i.website_url).length;

  return (
    <Card style={{ marginBottom: 16, padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 800, flexShrink: 0
          }}>AI</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
              KI-Sichtbarkeit (Add-on)
            </div>
            <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
              Salons in ChatGPT / Perplexity / Google AI sichtbar machen
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.primary, lineHeight: 1 }}>{enabled}<span style={{ fontSize: 12, opacity: 0.5 }}>/{total}</span></div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>aktiviert</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 60 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.success, lineHeight: 1 }}>{fullyLive}</div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>5/5 live</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: needSnippet > 0 ? COLORS.warning : COLORS.success, lineHeight: 1 }}>{needSnippet}</div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>Snippet fehlt</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 60 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, lineHeight: 1 }}>Ø {avgScore}</div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>Score</div>
          </div>
          <button
            onClick={() => setShowHelp(h => !h)}
            title="Hilfe anzeigen"
            style={{
              width: 28, height: 28, borderRadius: 14, border: "1px solid var(--border)",
              background: "var(--card)", color: COLORS.textLight, fontSize: 13, fontWeight: 700,
              cursor: "pointer"
            }}>?</button>
          <Btn size="sm" variant="ghost" onClick={() => navigate && navigate("admin-salons")}>Salons →</Btn>
        </div>
      </div>
      {noWebsite > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: COLORS.warning, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
          ⚠ {noWebsite} aktivierte{noWebsite === 1 ? "r" : ""} Salon{noWebsite === 1 ? "" : "s"} ohne Website-URL — Score nicht messbar. Im Salon-Edit-Modal hinterlegen.
        </div>
      )}
      {showHelp && (
        <div style={{
          marginTop: 12, padding: 14, background: "var(--cardHover)", borderRadius: 8,
          fontSize: 12, lineHeight: 1.6, color: COLORS.text, borderLeft: "3px solid " + COLORS.primary
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Wie funktioniert das?</div>
          <p style={{ margin: "0 0 8px" }}>
            <b>1. Aktivieren:</b> Im Salon-Edit-Modal die Checkbox „KI-Sichtbarkeit" anhaken + Salon-Website-URL eintragen. Default ist aus — Add-on kostet 9 €/Monat (Launch-Preis bis 30.06.2026, danach 19 €/Monat).
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <b>2. Score 0-5:</b> Live-Check pro Salon. +1 wenn robots.txt erreichbar, +1 pro KI-Bot der nicht geblockt ist (GPTBot, ClaudeBot/PerplexityBot, Google-Extended), +1 wenn Embed-Snippet auf Salon-Website eingebaut.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <b>3. Snippet:</b> Owner sieht im Dashboard den Code-Snippet + Button „An Webentwickler senden". Webentwickler baut ein → Score springt auf 5/5.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <b>4. Daily-Recheck:</b> Cron 04:15 prüft alle aktivierten Salons automatisch und aktualisiert Score.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <b>5. Aktivierungsanfragen:</b> Owner ohne Add-on klickt „Aktivieren" → Mail an support@shearly.de mit Salonname.
          </p>
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }}>
            <b>Was Salons noch tun müssen:</b> Webentwickler den Snippet in den &lt;head&gt; ihrer Website einbauen lassen (2 Minuten). Mehr nicht — Daten kommen automatisch aus Shearly.
          </div>
        </div>
      )}
    </Card>
  );
};

// AI_CITATIONS_v1 -- Admin-Mini-Widget: wird Salon in KI-Antworten genannt?
export const AdminAiCitationsMini = () => {
  const navigate = React.useContext(NavigateContext);
  const [items, setItems] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    api.request("/admin/ai-citations/overview").then(r => {
      if (r && r.ok) {
        setItems(r.data?.items || []);
        setTotalCost(r.data?.total_cost_7d_eur || 0);
      }
    }).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  const total = items.length;
  const enabled = items.filter(i => i.enabled).length;
  const totalChecks = items.reduce((s, i) => s + (i.checks_7d || 0), 0);
  const totalMentions = items.reduce((s, i) => s + (i.mentioned_7d || 0), 0);
  const avgRatio = totalChecks > 0 ? Math.round((totalMentions / totalChecks) * 100) : 0;

  return (
    <Card style={{ marginBottom: 16, padding: "14px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
            color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 800, flexShrink: 0
          }}>★</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, lineHeight: 1.2 }}>
              KI-Erwähnungen (Citation-Tracking)
            </div>
            <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
              Wird Salon bei „bester Friseur in …" in KI-Antworten genannt?
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.primary, lineHeight: 1 }}>{enabled}<span style={{ fontSize: 12, opacity: 0.5 }}>/{total}</span></div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>aktiviert</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.success, lineHeight: 1 }}>{totalMentions}<span style={{ fontSize: 12, opacity: 0.5 }}>/{totalChecks}</span></div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>Mentions 7d</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 60 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: avgRatio >= 50 ? COLORS.success : avgRatio >= 25 ? COLORS.warning : COLORS.danger, lineHeight: 1 }}>{avgRatio}%</div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>Trefferquote</div>
          </div>
          <div style={{ textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: COLORS.text, lineHeight: 1 }}>{totalCost.toFixed(2)}€</div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 3, letterSpacing: 0.3 }}>Cost 7d</div>
          </div>
          <button
            onClick={() => setShowHelp(h => !h)}
            title="Hilfe anzeigen"
            style={{
              width: 28, height: 28, borderRadius: 14, border: "1px solid var(--border)",
              background: "var(--card)", color: COLORS.textLight, fontSize: 13, fontWeight: 700,
              cursor: "pointer"
            }}>?</button>
        </div>
      </div>
      {showHelp && (
        <div style={{
          marginTop: 12, padding: 14, background: "var(--cardHover)", borderRadius: 8,
          fontSize: 12, lineHeight: 1.6, color: COLORS.text, borderLeft: "3px solid " + "#F59E0B"
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Was zeigt das?</div>
          <p style={{ margin: "0 0 8px" }}>
            <b>Citation-Tracking</b> (anders als „KI-Sichtbarkeit"): Wir fragen Claude mit Web-Search ganz konkret „bester Friseur in {`{Stadt}`}", „empfehlenswerter Friseursalon {`{Stadt}`}" — und prüfen, ob der Salon in der Antwort genannt wird.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <b>Aktivieren:</b> Pro Salon im Edit-Modal (oder via API `/api/admin/ai-citations/&lt;id&gt;/toggle`). Default = aus.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <b>Cron:</b> Daily 04:30. Pro Salon ~2 Queries × ~5 ct = ~10 ct/Tag = <b>~3 €/Salon/Monat</b>.
          </p>
          <p style={{ margin: 0 }}>
            <b>Marker:</b> AI_CITATIONS_v1
          </p>
        </div>
      )}
    </Card>
  );
};

// INVOICE_REMINDERS_v1 (17.09.2026) -- Erinnerung an wiederkehrende Rechnungen (z. B. Webseiten-Hosting
// Margot Duch, Jahresrechnung). Fällige Einträge (ab Vorlauf) stehen oben farbig, der Rest kompakt darunter.
// "Rechnung gestellt" schiebt das Datum um das Intervall weiter. Daten: /api/admin/invoice-reminders.
const INV_EMPTY_FORM = { customer_name: "", service: "", amount_eur: "", amount_note: "brutto", interval_months: 12, next_due: "", lead_days: 30, notes: "" };
const invFmtDate = (iso) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ""); return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || "—"); };
const invFmtEur = (v) => v == null ? "" : v.toFixed(2).replace(".", ",") + " €";
const invIntervalLabel = (n) => n === 12 ? "jährlich" : n === 1 ? "monatlich" : n === 3 ? "vierteljährlich" : n === 6 ? "halbjährlich" : `alle ${n} Monate`;

const InvoiceRemindersCard = () => {
  const isMobile = useIsMobile();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState(null);       // null = zu, sonst {id?, ...felder}
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState("");

  const load = () => {
    api.request("/admin/invoice-reminders").then(r => {
      if (r && r.ok) { setItems(r.data.reminders || []); setErr(""); }
      else setErr((r && r.data && r.data.error) || "Laden fehlgeschlagen");
    });
  };
  useEffect(load, []);

  const openNew = () => { setFormErr(""); setForm({ ...INV_EMPTY_FORM }); };
  const openEdit = (x) => { setFormErr(""); setForm({ ...x, amount_eur: x.amount_eur == null ? "" : String(x.amount_eur).replace(".", ",") }); };

  const save = async () => {
    setSaving(true); setFormErr("");
    const body = {
      customer_name: form.customer_name, service: form.service, amount_eur: form.amount_eur,
      amount_note: form.amount_note, interval_months: Number(form.interval_months), next_due: form.next_due,
      lead_days: Number(form.lead_days), notes: form.notes,
    };
    const r = form.id
      ? await api.request(`/admin/invoice-reminders/${form.id}`, { method: "PUT", body: JSON.stringify(body) })
      : await api.request("/admin/invoice-reminders", { method: "POST", body: JSON.stringify(body) });
    setSaving(false);
    if (r && r.ok) { setForm(null); load(); }
    else setFormErr((r && r.data && r.data.error) || "Speichern fehlgeschlagen");
  };

  const markInvoiced = async (x) => {
    if (!window.confirm(`Rechnung für ${x.customer_name} gestellt?\n\nDie nächste Erinnerung wird ${invIntervalLabel(x.interval_months)} weitergeschoben (fällig ab ${invFmtDate(x.next_due)} + ${x.interval_months} Monate).`)) return;
    const r = await api.request(`/admin/invoice-reminders/${x.id}/invoiced`, { method: "POST", body: "{}" });
    if (r && r.ok) load(); else window.alert((r && r.data && r.data.error) || "Fehlgeschlagen");
  };
  const remove = async (x) => {
    if (!window.confirm(`Erinnerung für ${x.customer_name} entfernen?\n\nEs kommt dann keine Erinnerung mehr für diese Rechnung.`)) return;
    const r = await api.request(`/admin/invoice-reminders/${x.id}`, { method: "DELETE" });
    if (r && r.ok) load(); else window.alert((r && r.data && r.data.error) || "Fehlgeschlagen");
  };

  const due = (items || []).filter(x => x.state !== "ok");
  const later = (items || []).filter(x => x.state === "ok");
  const whenText = (x) => x.days_left < 0 ? `seit ${-x.days_left} ${-x.days_left === 1 ? "Tag" : "Tagen"} überfällig`
    : x.days_left === 0 ? "heute fällig" : `fällig in ${x.days_left} ${x.days_left === 1 ? "Tag" : "Tagen"}`;
  const amountText = (x) => x.amount_eur == null ? "" : invFmtEur(x.amount_eur) + (x.amount_note ? " " + x.amount_note : "");
  const label = { display: "block", fontSize: 12, fontWeight: 600, color: COLORS.text, margin: "0 0 4px" };
  const input = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: "var(--text)" };

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Rechnungen stellen {due.length > 0 && <Badge text={`${due.length} fällig`} color={due.some(x => x.state === "overdue") ? COLORS.danger : COLORS.warning} />}</h3>
          <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2 }}>Wiederkehrende Rechnungen außerhalb von Stripe · Erinnerung ab Vorlauf hier und in der täglichen Admin-Mail</div>
        </div>
        <Btn size="sm" onClick={openNew}><Plus size={12} /> Eintrag</Btn>
      </div>

      {err && <div style={{ fontSize: 13, color: COLORS.danger }}>{err}</div>}
      {items === null && !err && <Skeleton width="100%" height={36} />}
      {items && items.length === 0 && <div style={{ fontSize: 13, color: COLORS.textLight, padding: "6px 0" }}>Noch keine Einträge.</div>}

      {due.map(x => {
        const c = x.state === "overdue" ? COLORS.danger : COLORS.warning;
        return (
          <div key={x.id} style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", flexDirection: isMobile ? "column" : "row", gap: 10, padding: "10px 12px", marginBottom: 8, borderRadius: 10, border: `1px solid ${c}`, borderLeft: `4px solid ${c}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>{x.customer_name}{x.service && <span style={{ fontWeight: 400, color: COLORS.textLight }}> · {x.service}</span>}</div>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                <strong style={{ color: c }}>{whenText(x)}</strong> · {invFmtDate(x.next_due)}{amountText(x) && <> · {amountText(x)}</>} · {invIntervalLabel(x.interval_months)}
              </div>
              {x.notes && <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 2, whiteSpace: "pre-wrap" }}>{x.notes}</div>}
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Btn size="sm" onClick={() => markInvoiced(x)}><Check size={12} /> Rechnung gestellt</Btn>
              <Btn size="sm" variant="secondary" onClick={() => openEdit(x)}><Settings size={12} /></Btn>
            </div>
          </div>
        );
      })}

      {later.length > 0 && (
        <div style={{ marginTop: due.length ? 6 : 0 }}>
          {later.map(x => (
            <div key={x.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderTop: `1px solid ${COLORS.border}`, fontSize: 12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{x.customer_name}</strong>{x.service && <span style={{ color: COLORS.textLight }}> · {x.service}</span>}
                <div style={{ color: COLORS.textLight, marginTop: 1 }}>
                  Nächste Rechnung {invFmtDate(x.next_due)}{amountText(x) && <> · {amountText(x)}</>} · {invIntervalLabel(x.interval_months)} · Erinnerung {x.lead_days} Tage vorher{x.last_invoiced_on && <> · zuletzt gestellt {invFmtDate(x.last_invoiced_on)}</>}
                </div>
              </div>
              <Btn size="sm" variant="secondary" onClick={() => openEdit(x)}><Settings size={12} /></Btn>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!form} onClose={() => !saving && setForm(null)} title={form && form.id ? "Erinnerung bearbeiten" : "Neue Rechnungs-Erinnerung"} width={480}>
        {form && (
          <div>
            <div style={{ marginBottom: 12 }}><label style={label}>Kunde *</label><input style={input} value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="z. B. HAAR(T)RAUM · Margot Duch" /></div>
            <div style={{ marginBottom: 12 }}><label style={label}>Leistung</label><input style={input} value={form.service} onChange={e => setForm({ ...form, service: e.target.value })} placeholder="z. B. Webseiten-Hosting + Pflege" /></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={label}>Betrag (€)</label><input style={input} inputMode="decimal" value={form.amount_eur} onChange={e => setForm({ ...form, amount_eur: e.target.value })} placeholder="z. B. 298,80" /></div>
              <div><label style={label}>Zusatz</label><input style={input} value={form.amount_note} onChange={e => setForm({ ...form, amount_note: e.target.value })} placeholder="brutto / netto" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div><label style={label}>Nächste Rechnung am *</label><input style={input} type="date" value={form.next_due} onChange={e => setForm({ ...form, next_due: e.target.value })} /></div>
              <div><label style={label}>Wiederholung</label>
                <select style={input} value={form.interval_months} onChange={e => setForm({ ...form, interval_months: Number(e.target.value) })}>
                  {[1, 3, 6, 12, 24].map(n => <option key={n} value={n}>{invIntervalLabel(n)}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}><label style={label}>Erinnerung (Tage vorher)</label><input style={input} type="number" min="0" max="365" value={form.lead_days} onChange={e => setForm({ ...form, lead_days: e.target.value })} /></div>
            <div style={{ marginBottom: 12 }}><label style={label}>Notiz</label><textarea style={{ ...input, minHeight: 60, fontFamily: "inherit" }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="z. B. Rechnung über Lexware, Laufzeit ab 16.09.2026" /></div>
            {formErr && <div style={{ fontSize: 13, color: COLORS.danger, marginBottom: 10 }}>{formErr}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              {form.id ? <Btn size="sm" variant="secondary" onClick={() => { const x = form; setForm(null); remove(x); }}><Trash2 size={12} /> Entfernen</Btn> : <span />}
              <div style={{ display: "flex", gap: 8 }}>
                <Btn size="sm" variant="secondary" onClick={() => setForm(null)} disabled={saving}>Abbrechen</Btn>
                <Btn size="sm" onClick={save} disabled={saving || !form.customer_name.trim() || !form.next_due}>{saving ? "Speichert…" : "Speichern"}</Btn>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export const AdminDashboardPage = () => {
  // ADMIN_DASHBOARD_REAL_v1 -- echte Daten aus DB statt hardcoded Demo
  // NAV_CONTEXT_FIX_v1 -- nutze NavigateContext statt nicht-zugreifbares setActivePage
  const navigate = React.useContext(NavigateContext);
  const goToSalons = () => navigate && navigate("admin-salons");
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api.request("/admin/dashboard-overview").then(r => {
      if (r && r.ok) setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const salons = data?.salons || [];
  const totals = data?.totals || { active_salons: 0, trial_salons: 0, mrr_eur: 0, ai_calls: 0, ai_cost_eur: 0 };
  const planColor = (plan) => {
    const p = (plan || "").toLowerCase();
    if (p === "premium") return COLORS.accent;
    if (p === "business" || p === "pro") return COLORS.primary;
    if (p === "starter") return "#8B5CF6";
    return COLORS.textLight;
  };
  const statusColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "active") return COLORS.success;
    if (s === "trial") return COLORS.warning;
    if (s === "cancelled") return COLORS.danger;
    return COLORS.textLight;
  };
  const statusLabel = (status) => {
    const s = (status || "").toLowerCase();
    return s === "active" ? "Aktiv" : s === "trial" ? "Trial" : s === "cancelled" ? "Gekündigt" : (status || "—");
  };

  return (
    <div>
      <Header title="Admin Dashboard" subtitle="Shearly Plattform — Alle Salons im Überblick" />

      {/* INVOICE_REMINDERS_v1 -- fällige Rechnungen ganz oben */}
      <InvoiceRemindersCard />

      {/* SYSTEM_STATS_v1 + ACTIVE_SESSIONS_v1 */}
      <ServerStatsWidget />
      <ActiveSalonsWidget />
      <AdminAiVisibilityMini />
      <AdminAiCitationsMini />

      {/* Stat-Cards: echte Aggregate aus DB (Monat) */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: window.innerWidth < 768 ? 8 : 12, marginBottom: window.innerWidth < 768 ? 16 : 24 }}>
        <StatCard icon={Building2} label="Aktive Salons" value={loading ? "…" : totals.active_salons} color={COLORS.success} />
        <StatCard icon={Users} label="Trial-Salons" value={loading ? "…" : totals.trial_salons} color={COLORS.warning} />
        <StatCard icon={Euro} label="MRR (monatl.)" value={loading ? "…" : `${totals.mrr_eur.toFixed(2)} €`} color={COLORS.primary} />
        <StatCard icon={PhoneCall} label="KI-Anrufe (Monat)" value={loading ? "…" : totals.ai_calls} color={COLORS.info} />
        <StatCard icon={Server} label="KI-Kosten (Monat)" value={loading ? "…" : `${totals.ai_cost_eur.toFixed(2)} €`} color={COLORS.danger} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card>
          <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>Marge pro Salon</h3>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 16px" }}>Einnahmen (Stripe oder Plan-Preis) minus KI-Kosten — diesen Monat</p>
          <div style={{ height: 220 }}>
            {loading ? <Skeleton width="100%" height={200} /> : salons.length === 0 ? (
              <div style={{ padding: 30, textAlign: "center", color: "var(--textLight)", fontSize: 13 }}>Noch keine Salons in der DB.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={salons.slice(0, 8).map(s => ({
                  name: (s.name || "").split(" ")[0].slice(0, 14),
                  einnahmen: s.revenue_eur,
                  kosten: s.ai_cost_eur,
                  marge: s.margin_eur,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="marge" fill={COLORS.success} name="Marge (€)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="kosten" fill={COLORS.danger} name="KI-Kosten (€)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card>
          <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>KI-Nutzung pro Salon</h3>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 16px" }}>Retell-Anrufe + Claude-Chats diesen Monat — Kosten in EUR</p>
          {loading ? (
            <>{[0,1,2].map(i => <div key={i} style={{ marginBottom: 8 }}><Skeleton width="100%" height={28} /></div>)}</>
          ) : salons.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "var(--textLight)", fontSize: 13 }}>Noch keine Salons.</div>
          ) : (
            salons.map(s => {
              const maxCalls = Math.max(50, ...salons.map(x => x.ai_calls || 0));
              return (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12 }}>
                  <strong style={{ width: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</strong>
                  <div style={{ flex: 1, height: 6, borderRadius: 3, background: COLORS.border }}>
                    <div style={{ width: `${Math.min((s.ai_calls / maxCalls) * 100, 100)}%`, height: "100%", borderRadius: 3, background: s.ai_calls > maxCalls * 0.7 ? COLORS.warning : COLORS.primary }} />
                  </div>
                  <span style={{ width: 70, textAlign: "right" }}>{s.ai_calls}×</span>
                  <span style={{ width: 60, textAlign: "right", color: COLORS.danger, fontWeight: 600 }}>{s.ai_cost_eur.toFixed(2)} €</span>
                </div>
              );
            })
          )}
        </Card>
      </div>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Alle Salons {!loading && `(${salons.length})`}</h3>
          <Btn size="sm" onClick={goToSalons}><Plus size={12} /> Neuen Salon anlegen</Btn>
        </div>
        {loading ? (
          <div>{[0,1,2,3].map(i => <div key={i} style={{ marginBottom: 8 }}><Skeleton width="100%" height={32} /></div>)}</div>
        ) : salons.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "var(--textLight)", fontSize: 13 }}>Noch keine Salons in der Datenbank.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {["Salon", "Stadt", "Plan", "Status", "Mitarb.", "KI-Anrufe", "KI-Kosten", "Einnahmen", "Marge", "Aktionen"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {salons.map(s => (
                <tr key={s.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "10px 10px", fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: "10px 10px" }}>{s.city || "—"}</td>
                  <td style={{ padding: "10px 10px" }}><Badge text={s.plan || "—"} color={planColor(s.plan)} /></td>
                  <td style={{ padding: "10px 10px" }}><Badge text={statusLabel(s.status)} color={statusColor(s.status)} /></td>
                  <td style={{ padding: "10px 10px" }}>{s.employees}</td>
                  <td style={{ padding: "10px 10px" }}>{s.ai_calls}</td>
                  <td style={{ padding: "10px 10px", color: COLORS.danger, fontWeight: 600 }}>{s.ai_cost_eur.toFixed(2)} €</td>
                  <td style={{ padding: "10px 10px", fontWeight: 600 }}>{s.revenue_eur.toFixed(2)} €</td>
                  <td style={{ padding: "10px 10px", fontWeight: 700, color: s.margin_eur >= 0 ? COLORS.success : COLORS.danger }}>{s.margin_eur.toFixed(2)} €</td>
                  <td style={{ padding: "10px 10px" }}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <Btn size="sm" variant="secondary" onClick={goToSalons}><Eye size={10} /></Btn>
                      <Btn size="sm" variant="secondary" onClick={goToSalons}><Settings size={10} /></Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 12, fontSize: 11, color: "var(--textLight)" }}>
          Daten aus DB · Zeitraum: aktueller Monat ({data?.year_month || "–"}) · KI-Kosten = Retell + Claude-Chat · Einnahmen = Stripe-real wenn vorhanden, sonst Plan-Preis (nur fuer zahlende Salons)
        </div>
      </Card>
    </div>
  );
};

// Admin: System-Monitor
export const AdminMonitorPage = () => {
  const isMobile = useIsMobile();  // MOBILE_FIX_v1
  const [health, setHealth] = useState(null);
  const [salonStatus, setSalonStatus] = useState(null);
  const [errors, setErrors] = useState([]);
  const [alertPhone, setAlertPhone] = useState("");
  const [alertSaved, setAlertSaved] = useState(false);
  // OPS_SUMMARY_v1 -- konsolidierte Operations-Übersicht
  const [opsSummary, setOpsSummary] = useState(null);
  // ALERT_TEST_v1
  const [alertTestSending, setAlertTestSending] = useState(false);
  const [alertTestResult, setAlertTestResult] = useState(null);
  // ALERT_SYSTEM_v1 -- Daily-Summary-Toggle
  const [dailySummaryEnabled, setDailySummaryEnabled] = useState(true);
  const [dailySummaryToggling, setDailySummaryToggling] = useState(false);
  React.useEffect(() => {
    api.request("/admin/alerts/summary-toggle").then(r => {
      if (r && r.ok) setDailySummaryEnabled(!!r.data.daily_summary_enabled);
    });
  }, []);
  const [loading, setLoading] = useState(true);
  const token = sessionStorage.getItem("sp_token");

  const loadData = async () => {
    try {
      const [healthRes, statusRes, errorsRes, alertsRes, opsRes] = await Promise.all([
        fetch("/api/health"),
        fetch("/api/admin/salon-status", { headers: { Authorization: "Bearer " + token } }),
        fetch("/api/admin/errors?limit=50", { headers: { Authorization: "Bearer " + token } }),
        fetch("/api/admin/alerts", { headers: { Authorization: "Bearer " + token } }),
        fetch("/api/admin/ops-summary", { headers: { Authorization: "Bearer " + token } }),
      ]);
      setHealth(await healthRes.json());
      setSalonStatus(await statusRes.json());
      const errData = await errorsRes.json();
      setErrors(errData.errors || []);
      const alertData = await alertsRes.json();
      setAlertPhone(alertData.admin_phone || "");
      const opsData = await opsRes.json();
      setOpsSummary(opsData);
    } catch (e) { console.error("Monitor load error:", e); }
    setLoading(false);
  };

  useEffect(() => { loadData(); const iv = setInterval(loadData, 30000); return () => clearInterval(iv); }, []);

  // ADMIN_ALERT_SAVE_FIX_v1 -- prueft Response, zeigt normalisierte Form,
  // erlaubt explizit "leer = entfernen". Frueher: silent fake-success.
  const [alertSaveError, setAlertSaveError] = useState("");
  const saveAlert = async () => {
    setAlertSaveError("");
    try {
      const res = await fetch("/api/admin/alerts", {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ phone: alertPhone.trim() })
      });
      if (!res.ok) {
        setAlertSaveError(`Fehler beim Speichern (HTTP ${res.status})`);
        return;
      }
      const data = await res.json().catch(() => ({}));
      // Backend gibt normalisierte Form zurueck — uebernehmen damit UI persistiert
      if (typeof data.phone === "string") setAlertPhone(data.phone);
      setAlertSaved(true);
      setTimeout(() => setAlertSaved(false), 3000);
    } catch (e) {
      setAlertSaveError("Netzwerk-Fehler: " + (e && e.message ? e.message : "unbekannt"));
    }
  };

  const clearErrors = async () => {
    await fetch("/api/admin/errors", { method: "DELETE", headers: { Authorization: "Bearer " + token } });
    setErrors([]);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Lade System-Status...</div>;

  const statusColor = { ok: "#22c55e", degraded: "#f59e0b", error: "#ef4444" };
  const healthColor = { healthy: "#22c55e", warning: "#f59e0b", critical: "#ef4444", inactive: "#9ca3af" };
  const healthLabel = { healthy: "Gesund", warning: "Warnung", critical: "Kritisch", inactive: "Inaktiv" };

  return (
    <div>
      <Header title="System-Monitor" subtitle="Live-Status aller Systeme & Salons" />

      {/* System Health Overview */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
        <div style={{ background: "var(--card)", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>System-Status</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: statusColor[health?.status] || "#9ca3af" }} />
            <span style={{ fontSize: 20, fontWeight: 700 }}>{health?.status === "ok" ? "Alles OK" : health?.status === "degraded" ? "Eingeschränkt" : "Fehler"}</span>
          </div>
        </div>
        {health?.checks && Object.entries(health.checks).map(([key, val]) => {
          if (typeof val !== "object") return (
            <div key={key} style={{ background: "var(--card)", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>{key}</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{val}</div>
            </div>
          );
          return (
            <div key={key} style={{ background: "var(--card)", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>{key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: val.status === "ok" ? "#22c55e" : val.status === "not_configured" ? "#9ca3af" : "#ef4444" }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>{val.status === "ok" ? "Verbunden" : val.status === "not_configured" ? "Nicht konfiguriert" : "Fehler"}</span>
              </div>
              {val.salons !== undefined && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{val.salons} Salons</div>}
              {val.free_gb !== undefined && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{val.free_gb} GB frei</div>}
              {val.phone && <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{val.phone}</div>}
            </div>
          );
        })}
      </div>

      {/* OPS_SUMMARY_v1 -- Operations-Wall: konsolidierte Live-Metriken */}
      {opsSummary && !opsSummary.error && (
        <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 16px" }}>📊 Operations heute</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
            {[
              { label: "Buchungen heute", value: (opsSummary.bookings_today && opsSummary.bookings_today.total) || 0,
                sub: opsSummary.bookings_today ? `Online ${opsSummary.bookings_today.online} · Manuell ${opsSummary.bookings_today.manual} · KI-Tele ${opsSummary.bookings_today.ai_phone || 0}` : "" },
              { label: "Neue Kunden", value: opsSummary.new_customers_today || 0, sub: "" },
              { label: "SMS-Outbox", value: (opsSummary.sms_outbox && opsSummary.sms_outbox.pending) || 0,
                sub: opsSummary.sms_outbox ? `${opsSummary.sms_outbox.sent_today} heute gesendet${opsSummary.sms_outbox.failed_total > 0 ? " · " + opsSummary.sms_outbox.failed_total + " failed" : ""}` : "",
                warn: (opsSummary.sms_outbox && opsSummary.sms_outbox.failed_total > 0) },
              { label: "KI-Kosten Monat", value: (opsSummary.ai_cost_month_eur || 0).toFixed(2) + " €", sub: "" },
              { label: "Aktive Salons", value: opsSummary.active_salons || 0, sub: "" },
              { label: "Audit-Events heute", value: opsSummary.audit_events_today || 0, sub: "" },
              { label: "Failed Logins heute", value: opsSummary.failed_logins_today || 0,
                sub: "", warn: (opsSummary.failed_logins_today || 0) > 5 },
              { label: "DB-Größe", value: (opsSummary.db_size_mb || 0) + " MB", sub: "" },
            ].map((m, i) => (
              <div key={i} style={{
                padding: 14, background: m.warn ? "rgba(239,68,68,0.10)" : "var(--cardHover)",
                borderRadius: 10, border: m.warn ? "1px solid rgba(239,68,68,0.35)" : "1px solid transparent"
              }}>
                <div style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: m.warn ? "#EF4444" : COLORS.text, fontVariantNumeric: "tabular-nums" }}>{m.value}</div>
                {m.sub && <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4 }}>{m.sub}</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: COLORS.textLight, textAlign: "right" }}>
            Auto-Refresh alle 30s · letzte Aktualisierung: {opsSummary.timestamp ? new Date(opsSummary.timestamp).toLocaleTimeString("de-DE") : "—"}
          </div>
        </div>
      )}

      {/* Salon Health Table */}
      <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Salon-Status Live</h3>
          <button onClick={loadData} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)" /* DARK_BTN_TEXT_v1 */, cursor: "pointer", fontSize: 13 }}>
            <RefreshCw size={14} style={{ marginRight: 4 }} /> Aktualisieren
          </button>
        </div>
        {/* MOBILE_FIX_v1 -- Tabelle in overflow-x-Wrapper damit Mobile-Page nicht horizontal scrollt */}
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -12px", padding: "0 12px" }}>
        <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border)" }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>Salon</th>
              <th style={{ textAlign: "center", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>Health</th>
              <th style={{ textAlign: "center", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>Fehler (1h)</th>
              <th style={{ textAlign: "center", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>Buchungen (24h)</th>
              <th style={{ textAlign: "center", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>User aktiv</th>
              <th style={{ textAlign: "center", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>Booking</th>
              <th style={{ textAlign: "center", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>KI-Tel.</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: "var(--textLight)", fontWeight: 600, whiteSpace: "nowrap" }}>Probleme</th>
            </tr>
          </thead>
          <tbody>
            {(salonStatus?.salons || []).map(s => (
              <tr key={s.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{s.name}<br/><span style={{ fontSize: 12, color: "#9ca3af" }}>/b/{s.slug}</span></td>
                <td style={{ textAlign: "center", padding: "10px 12px" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: healthColor[s.health] + "18", color: healthColor[s.health] }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: healthColor[s.health] }} />
                    {healthLabel[s.health] || s.health}
                  </span>
                </td>
                <td style={{ textAlign: "center", padding: "10px 12px", color: s.errors_last_hour > 0 ? "#ef4444" : "#22c55e", fontWeight: 600 }}>{s.errors_last_hour}</td>
                <td style={{ textAlign: "center", padding: "10px 12px" }}>{s.bookings_last_24h}</td>
                <td style={{ textAlign: "center", padding: "10px 12px" }}>{s.active_users}</td>
                <td style={{ textAlign: "center", padding: "10px 12px", color: s.online_booking ? "#22c55e" : "#9ca3af" }}>{s.online_booking ? "Aktiv" : "—"}</td>
                <td style={{ textAlign: "center", padding: "10px 12px", color: s.ai_telephony ? "#22c55e" : "#9ca3af" }}>{s.ai_telephony ? "Aktiv" : "—"}</td>
                <td style={{ padding: "10px 12px", fontSize: 12, color: "#ef4444" }}>{s.issues?.join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>{/* /MOBILE_FIX_v1 overflow-x */}
      </div>

      {/* Error Log */}
      <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Fehler-Log <span style={{ fontSize: 14, fontWeight: 400, color: "#6b7280" }}>({errors.length} Einträge)</span></h3>
          {errors.length > 0 && (
            <button onClick={clearErrors} style={{ padding: "6px 14px", borderRadius: 8, border: "1px solid #fecaca", background: "rgba(239,68,68,0.15)", color: "#ef4444", cursor: "pointer", fontSize: 13 }}>
              Log leeren
            </button>
          )}
        </div>
        {errors.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "#22c55e" }}>
            <CheckCircle size={32} style={{ margin: "0 auto 8px" }} />
            <div>Keine Fehler — alles läuft!</div>
          </div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: "auto" }}>
            {errors.map((e, i) => (
              <div key={i} style={{ padding: "10px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 13 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: e.severity === "critical" ? "#fef2f2" : e.severity === "warning" ? "#fffbeb" : "#fef2f2",
                    color: e.severity === "critical" ? "#ef4444" : e.severity === "warning" ? "#f59e0b" : "#ef4444"
                  }}>{e.severity?.toUpperCase()}</span>
                  <span style={{ color: "#6b7280" }}>{new Date(e.timestamp).toLocaleString("de-DE")}</span>
                  <span style={{ color: "#9ca3af" }}>Salon #{e.salon_id}</span>
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 12 }}>
                  <strong>{e.endpoint}</strong>: {e.error}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      
      {/* Alert Settings — ALERT_TEST_v1: Test-SMS-Button + bessere Erklaerung */}
      <Card>
        <h3 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 8px" }}>Alert-Einstellungen</h3>
        <p style={{ fontSize: 12, color: "var(--textLight)", marginBottom: 14, lineHeight: 1.6 }}>
          Bei <strong>kritischen Fehlern</strong> (z.B. Backend-Crash, Datenbankfehler, Salon-Ausfall) bekommst du sofort eine SMS an diese Nummer. So merkst du Probleme bevor sich Salons beschweren. Standard-Errors (kleine Fehler die User selber wegklicken) lösen <em>keinen</em> Alert aus — sonst wäre dein Handy zugespammt.
        </p>
        {/* MOBILE_FIX_v1 -- Mobile column-layout, Desktop row. Vorher: flex:1 ohne minWidth -> Buttons drueckten Input auf 0px breit, Nummer abgeschnitten. */}
        <div style={{ display: "flex", gap: 12, alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>SMS-Nummer:</label>
          <input type="tel" value={alertPhone} onChange={e => setAlertPhone(e.target.value)}
            placeholder="+49 170 1234567"
            style={{ flex: 1, minWidth: 200, maxWidth: isMobile ? "100%" : 260, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--card)", color: "var(--text)", fontSize: 14, boxSizing: "border-box" }} />
          <Btn variant="primary" onClick={saveAlert}>
            <Check size={14} /> Speichern
          </Btn>
          <Btn variant="secondary" disabled={alertTestSending || !alertPhone} onClick={async () => {
            setAlertTestSending(true);
            setAlertTestResult(null);
            const r = await api.request("/admin/alerts/test", { method: "POST" });
            setAlertTestSending(false);
            if (r && r.ok && r.data && r.data.sent) {
              setAlertTestResult({ ok: true, msg: `✓ Test-SMS an ${r.data.phone_masked} verschickt. Sollte in 5-15 Sek ankommen.` });
            } else {
              setAlertTestResult({ ok: false, msg: "✗ " + ((r && r.data && r.data.error) || "Unbekannter Fehler") });
            }
          }}>
            <MessageSquare size={14} /> {alertTestSending ? "Sende…" : "Test-SMS senden"}
          </Btn>
          {alertSaved && <span style={{ color: "#22c55e", fontSize: 13, fontWeight: 600 }}>Gespeichert!</span>}
          {alertSaveError && <span style={{ color: "#ef4444", fontSize: 13, fontWeight: 600 }}>{alertSaveError}</span>}
        </div>
        {alertTestResult && (
          <div style={{ marginTop: 14, padding: 10, borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: alertTestResult.ok ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)",
            color: alertTestResult.ok ? "#10B981" : "#EF4444",
            border: `1px solid ${alertTestResult.ok ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}` }}>
            {alertTestResult.msg}
          </div>
        )}

        {/* ALERT_SYSTEM_v1 -- Daily-Summary-Toggle */}
        <div style={{ marginTop: 24, paddingTop: 18, borderTop: `1px solid ${COLORS.border}` }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, margin: "0 0 6px" }}>📧 Daily-Summary-Mail</h4>
          <p style={{ fontSize: 12, color: "var(--textLight)", margin: "0 0 12px", lineHeight: 1.6 }}>
            Jeden Morgen um 9:00 Uhr eine Zusammenfassung per E-Mail: aktive Salons, Top-Frontend-Errors, Trial-Ablaeufe, Disk/RAM/Backup-Status. Anders als die SMS-Alerts: kein akuter Alarm, sondern <strong>Tages-Bericht</strong> zum Frühstück. Ausschalten wenn die zu viel ist.
          </p>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "8px 12px", background: "var(--cardHover)", borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
            <input type="checkbox" checked={dailySummaryEnabled} disabled={dailySummaryToggling}
              onChange={async e => {
                const next = e.target.checked;
                setDailySummaryToggling(true);
                const r = await api.request("/admin/alerts/summary-toggle", { method: "PUT", body: JSON.stringify({ enabled: next }) });
                setDailySummaryToggling(false);
                if (r && r.ok) setDailySummaryEnabled(!!r.data.daily_summary_enabled);
              }} style={{ accentColor: COLORS.primary }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>{dailySummaryEnabled ? "Aktiv — Mail kommt täglich um 9:00" : "Deaktiviert — keine Mail"}</span>
          </label>
        </div>
      </Card>
    </div>
  );
};


// PRICING_FRONTEND_v1 — Plan-Pricing zentrale Verwaltung (SSOT)
export const AdminPricingPage = () => {
  const [plans, setPlans] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editingSlug, setEditingSlug] = React.useState(null);
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.request("/admin/pricing/plans");
      if (r && r.ok && Array.isArray(r.data)) { setPlans(r.data); }
      else if (r && r.data && r.data.error) { setError("Fehler: " + r.data.error); setPlans([]); }
      else { setError("HTTP " + (r ? r.status : "?") + " — keine Daten"); setPlans([]); }
    } catch (e) { setError("Fehler beim Laden: " + (e.message || e)); setPlans([]); } /* API_GUARD_v2 */
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const startEdit = (p) => { setEditingSlug(p.slug); setDraft({...p}); };
  const cancelEdit = () => { setEditingSlug(null); setDraft({}); };

  const save = async () => {
    setSaving(true); setError("");
    try {
      await api.request("/admin/pricing/plans/" + editingSlug, {
        method: "POST",
        body: JSON.stringify({
          display_name: draft.display_name,
          monthly_eur: parseFloat(draft.monthly_eur) || 0,
          monthly_ki_eur: parseFloat(draft.monthly_ki_eur) || 0,
          yearly_eur: parseFloat(draft.yearly_eur) || 0,
          yearly_ki_eur: parseFloat(draft.yearly_ki_eur) || 0,
          employees_label: draft.employees_label || "",
          max_employees: (draft.max_employees === "" || draft.max_employees == null || isNaN(parseInt(draft.max_employees))) ? null : parseInt(draft.max_employees), /* EMPLOYEE_LIMIT_v1 -- leer = unbegrenzt */
          features: draft.features || [],
          popular: !!draft.popular,
          active: !!draft.active,
          sort_order: parseInt(draft.sort_order) || 100,
          notes: draft.notes || "",
          stripe_price_id_monthly: draft.stripe_price_id_monthly || "",
          stripe_price_id_monthly_ki: draft.stripe_price_id_monthly_ki || "",
          stripe_price_id_yearly: draft.stripe_price_id_yearly || "",
          stripe_price_id_yearly_ki: draft.stripe_price_id_yearly_ki || ""
        })
      });
      setEditingSlug(null); setDraft({});
      await load();
    } catch (e) { setError("Fehler beim Speichern: " + (e.message || e)); }
    setSaving(false);
  };

  if (loading) return <div style={{padding:24, color:"var(--textLight)"}}>Lade Pläne...</div>;

  return (
    <div>
      <Header title="Preise & Pläne" subtitle="Plan-Pricing zentral verwalten — Single Source of Truth für Salon-Anlage, KI-Empfang und Marge-Berechnung. Alle Beträge NETTO — Stripe bucht +19 % MwSt. ab (NETTO_PRICING_v1)" />
      {error && <div style={{padding:12, background:COLORS.danger+"22", color:COLORS.danger, borderRadius:8, marginBottom:16}}>{error}</div>}
      <div style={{padding:12, background:"var(--card)", borderRadius:8, marginBottom:16, fontSize:12, color:"var(--textLight)", border:"1px solid var(--border)"}}>
        Änderungen gelten sofort für alle Frontends (Cache-TTL 60 Sek). Stripe-Price-IDs nur ändern wenn die entsprechenden Prices im Stripe-Dashboard existieren — sonst bricht der Checkout.
      </div>
      <div style={{background:"var(--card)", borderRadius:12, border:"1px solid var(--border)", overflow:"auto"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:13}}>
          <thead>
            <tr style={{background:"var(--cardHover)", textAlign:"left"}}>
              <th style={{padding:10}}>Slug</th>
              <th style={{padding:10}}>Name</th>
              <th style={{padding:10}}>Plätze</th>
              <th style={{padding:10, textAlign:"right"}}>Max. MA</th>
              <th style={{padding:10, textAlign:"right"}}>Mt €</th>
              <th style={{padding:10, textAlign:"right"}}>Mt+KI €</th>
              <th style={{padding:10, textAlign:"right"}}>Jahr €</th>
              <th style={{padding:10, textAlign:"right"}}>Jahr+KI €</th>
              <th style={{padding:10, textAlign:"center"}}>Beliebt</th>
              <th style={{padding:10, textAlign:"center"}}>Aktiv</th>
              <th style={{padding:10}}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {plans.map(p => editingSlug === p.slug ? (
              <React.Fragment key={p.slug}>
                <tr style={{borderTop:"1px solid var(--border)", background:"var(--cardHover)"}}>
                  <td style={{padding:8}}><code style={{fontSize:11, color:"var(--textLight)"}}>{p.slug}</code></td>
                  <td style={{padding:6}}><input value={draft.display_name||""} onChange={e=>setDraft({...draft, display_name:e.target.value})} style={{width:"95%", padding:6}} /></td>
                  <td style={{padding:6}}><input value={draft.employees_label||""} onChange={e=>setDraft({...draft, employees_label:e.target.value})} style={{width:"95%", padding:6}} /></td>
                  {/* EMPLOYEE_LIMIT_v1 -- leer = unbegrenzt */}
                  <td style={{padding:6}}><input type="number" min="0" placeholder="∞" value={draft.max_employees == null ? "" : draft.max_employees} onChange={e=>setDraft({...draft, max_employees:e.target.value})} style={{width:60, padding:6, textAlign:"right"}} /></td>
                  <td style={{padding:6}}><input type="number" step="0.01" value={draft.monthly_eur||0} onChange={e=>setDraft({...draft, monthly_eur:e.target.value})} style={{width:80, padding:6, textAlign:"right"}} /></td>
                  <td style={{padding:6}}><input type="number" step="0.01" value={draft.monthly_ki_eur||0} onChange={e=>setDraft({...draft, monthly_ki_eur:e.target.value})} style={{width:80, padding:6, textAlign:"right"}} /></td>
                  <td style={{padding:6}}><input type="number" step="0.01" value={draft.yearly_eur||0} onChange={e=>setDraft({...draft, yearly_eur:e.target.value})} style={{width:80, padding:6, textAlign:"right"}} /></td>
                  <td style={{padding:6}}><input type="number" step="0.01" value={draft.yearly_ki_eur||0} onChange={e=>setDraft({...draft, yearly_ki_eur:e.target.value})} style={{width:80, padding:6, textAlign:"right"}} /></td>
                  <td style={{padding:6, textAlign:"center"}}><input type="checkbox" checked={!!draft.popular} onChange={e=>setDraft({...draft, popular:e.target.checked})} /></td>
                  <td style={{padding:6, textAlign:"center"}}><input type="checkbox" checked={!!draft.active} onChange={e=>setDraft({...draft, active:e.target.checked})} /></td>
                  <td style={{padding:6}}>
                    <button onClick={save} disabled={saving} style={{padding:"6px 12px", borderRadius:4, border:"none", background:COLORS.success, color:"#fff", cursor:"pointer", marginRight:4}}>{saving ? "..." : "✓ Speichern"}</button>
                    <button onClick={cancelEdit} style={{padding:"6px 10px", borderRadius:4, border:"1px solid var(--border)", background:"var(--card)", color:"var(--text)", cursor:"pointer"}}>Abbruch</button>
                  </td>
                </tr>
                <tr style={{background:"var(--cardHover)"}}>
                  <td colSpan={11} style={{padding:"8px 12px", fontSize:12, color:"var(--textLight)", borderBottom:"1px solid var(--border)"}}>
                    <details>
                      <summary style={{cursor:"pointer", color:COLORS.warning, padding:"4px 0"}}>Stripe-Price-IDs (Erweitert) — nur ändern wenn die Prices im Stripe-Dashboard existieren</summary>
                      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:8}}>
                        <label><div style={{color:"var(--textLight)", fontSize:11}}>Monatlich</div><input value={draft.stripe_price_id_monthly||""} onChange={e=>setDraft({...draft, stripe_price_id_monthly:e.target.value})} style={{width:"100%", padding:6, fontFamily:"monospace", fontSize:11}} /></label>
                        <label><div style={{color:"var(--textLight)", fontSize:11}}>Monatlich+KI</div><input value={draft.stripe_price_id_monthly_ki||""} onChange={e=>setDraft({...draft, stripe_price_id_monthly_ki:e.target.value})} style={{width:"100%", padding:6, fontFamily:"monospace", fontSize:11}} /></label>
                        <label><div style={{color:"var(--textLight)", fontSize:11}}>Jährlich</div><input value={draft.stripe_price_id_yearly||""} onChange={e=>setDraft({...draft, stripe_price_id_yearly:e.target.value})} style={{width:"100%", padding:6, fontFamily:"monospace", fontSize:11}} /></label>
                        <label><div style={{color:"var(--textLight)", fontSize:11}}>Jährlich+KI</div><input value={draft.stripe_price_id_yearly_ki||""} onChange={e=>setDraft({...draft, stripe_price_id_yearly_ki:e.target.value})} style={{width:"100%", padding:6, fontFamily:"monospace", fontSize:11}} /></label>
                      </div>
                    </details>
                  </td>
                </tr>
              </React.Fragment>
            ) : (
              <tr key={p.slug} style={{borderTop:"1px solid var(--border)"}}>
                <td style={{padding:10}}><code style={{fontSize:11, color:"var(--textLight)"}}>{p.slug}</code></td>
                <td style={{padding:10, fontWeight:600}}>{p.display_name}{p.popular && <span style={{fontSize:10, padding:"2px 6px", background:COLORS.primary, color:"#fff", borderRadius:4, marginLeft:6}}>BELIEBT</span>}</td>
                <td style={{padding:10, color:"var(--textLight)", fontSize:12}}>{p.employees_label}</td>
                <td style={{padding:10, textAlign:"right"}}>{p.max_employees == null ? "∞" : p.max_employees}</td>
                <td style={{padding:10, textAlign:"right", fontWeight:600}}>{(+p.monthly_eur).toFixed(2)}</td>
                <td style={{padding:10, textAlign:"right"}}>{(+p.monthly_ki_eur).toFixed(2)}</td>
                <td style={{padding:10, textAlign:"right", color:"var(--textLight)"}}>{(+p.yearly_eur).toFixed(2)}</td>
                <td style={{padding:10, textAlign:"right", color:"var(--textLight)"}}>{(+p.yearly_ki_eur).toFixed(2)}</td>
                <td style={{padding:10, textAlign:"center"}}>{p.popular ? "✓" : <span style={{color:"var(--textLight)"}}>—</span>}</td>
                <td style={{padding:10, textAlign:"center", color: p.active ? COLORS.success : COLORS.danger}}>{p.active ? "✓" : "✕"}</td>
                <td style={{padding:10}}>
                  <button onClick={()=>startEdit(p)} style={{padding:"6px 12px", borderRadius:4, border:"1px solid var(--border)", background:"var(--card)", color:"var(--text)", cursor:"pointer", fontSize:12}}>Bearbeiten</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {plans.length === 0 && <div style={{padding:24, textAlign:"center", color:"var(--textLight)"}}>Keine Pläne in der Datenbank.</div>}
      </div>
    </div>
  );
};

// Admin: KI-Kosten & Metriken
// COSTS_FRONTEND_v1 — Drittanbieter-Kostenanalyse pro Salon (Pricing-Validation-Tool)
export const AdminCostsPage = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [range, setRange] = React.useState("30d");
  const [ym, setYm] = React.useState(new Date().toISOString().slice(0,7));
  const [syncing, setSyncing] = React.useState(false);
  const [syncMsg, setSyncMsg] = React.useState("");
  const [showPlatformForm, setShowPlatformForm] = React.useState(false);
  const [platformDraft, setPlatformDraft] = React.useState({hostinger_eur:0, uptimerobot_eur:0, backblaze_eur:0, other_eur:0, notes:""});
  const [savingPlatform, setSavingPlatform] = React.useState(false);
  const [sortBy, setSortBy] = React.useState("marge_asc");
  const [balances, setBalances] = React.useState(null); // PROVIDER_BALANCES_v1
  React.useEffect(() => {
    api.request("/admin/provider-balances").then(r => { if (r && r.ok && r.data) setBalances(r.data); }).catch(() => {});
  }, []);

  const fmt = (n) => Number(n||0).toFixed(2);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.request("/admin/costs/full?range=" + range + "&ym=" + ym);
      if (!r || !r.ok || !r.data || !r.data.salons) { console.error("costs/full bad:", r); return; }
      const d = r.data;
      setData(d);
      setPlatformDraft({
        hostinger_eur: d.platform_costs.hostinger_eur || 0,
        uptimerobot_eur: d.platform_costs.uptimerobot_eur || 0,
        backblaze_eur: d.platform_costs.backblaze_eur || 0,
        other_eur: d.platform_costs.other_eur || 0,
        notes: d.platform_costs.notes || ""
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  React.useEffect(() => { load(); }, [range, ym]);

  const refresh = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      const r = await api.request("/admin/costs/refresh", {method:"POST", body: JSON.stringify({since_days: 90})});
      setSyncMsg(r && r.ok && r.data ? `✓ ${r.data.synced.twilio} SMS, ${r.data.synced.stripe} Charges, ${r.data.synced.retell} Retell-Calls (${r.data.took_ms}ms)` : `✗ Sync fehlgeschlagen (HTTP ${r ? r.status : "?"})`);
      await load();
    } catch (e) { setSyncMsg("✗ Fehler: " + (e.message || e)); }
    setSyncing(false);
  };

  const savePlatform = async () => {
    setSavingPlatform(true);
    try {
      await api.request("/admin/platform-costs/" + ym, {method:"POST", body: JSON.stringify(platformDraft)});
      setShowPlatformForm(false);
      await load();
    } catch (e) { console.error(e); }
    setSavingPlatform(false);
  };

  const exportCsv = () => {
    if (!data) return;
    const headers = ["Salon","Stadt","Plan","Status","Retell EUR","Retell Calls","Claude EUR","Claude Chats","Claude In-Tok","Claude Out-Tok","Twilio EUR","Twilio SMS","Stripe Revenue","Stripe Fees","Plattform-Anteil","Gesamtkosten","Einnahmen","Marge","Min-Preis"];
    const rows = data.salons.map(s => [s.name, s.city||"", s.plan||"", s.status||"", s.retell_eur, s.retell_calls, s.anthropic_eur||0, s.anthropic_chats||0, s.anthropic_in_tokens||0, s.anthropic_out_tokens||0, s.twilio_eur, s.twilio_count, s.stripe_revenue_eur, s.stripe_fees_eur, s.platform_share_eur, s.total_cost_eur, s.revenue_eur, s.marge_eur, s.breakeven_min_eur]);
    const csv = [headers, ...rows].map(r => r.map(c => '"' + String(c).replace(/"/g,'""') + '"').join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], {type:"text/csv;charset=utf-8"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "shearly_costs_" + range + "_" + ym + ".csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const sortedSalons = React.useMemo(() => {
    if (!data) return [];
    const arr = [...data.salons];
    if (sortBy === "marge_asc") arr.sort((a,b) => a.marge_eur - b.marge_eur);
    else if (sortBy === "marge_desc") arr.sort((a,b) => b.marge_eur - a.marge_eur);
    else if (sortBy === "cost_desc") arr.sort((a,b) => b.total_cost_eur - a.total_cost_eur);
    else if (sortBy === "name") arr.sort((a,b) => (a.name||"").localeCompare(b.name||""));
    return arr;
  }, [data, sortBy]);

  if (loading && !data) return <div style={{padding:24, color:"var(--textLight)"}}>Lade Kostenübersicht...</div>;
  if (!data) return <div style={{padding:24, color:COLORS.danger}}>Fehler beim Laden.</div>;

  const ranges = [["today","Heute"],["30d","30 Tage"],["3m","3 Monate"],["12m","12 Monate"],["all","Gesamt"]];
  const sorts = [["marge_asc","Marge ↑ (Risiko)"],["marge_desc","Marge ↓"],["cost_desc","Kosten ↓"],["name","Name"]];

  return (
    <div>
      <Header title="KI-Kosten & Metriken" subtitle="Drittanbieter-Kosten pro Salon · Pricing-Validation · Refresh zieht Twilio/Stripe/Retell live" />

      {/* PROVIDER_BALANCES_v1 — Live-Guthaben-Banner */}
      {balances && (
        <div style={{display:"flex", flexWrap:"wrap", gap:12, padding:"10px 14px", background:"var(--card)", borderRadius:8, border:"1px solid var(--border)", marginBottom:16, fontSize:13}}>
          <span style={{color:"var(--textLight)"}}>Provider-Guthaben:</span>
          <span><strong style={{color: balances.twilio.ok ? (balances.twilio.balance < 5 ? COLORS.danger : balances.twilio.balance < 20 ? COLORS.warning : COLORS.success) : "var(--textLight)"}}>{balances.twilio.ok ? `${balances.twilio.balance.toFixed(2)} ${balances.twilio.currency}` : "—"}</strong> Twilio</span>
          <span style={{color:"var(--textLight)"}}>·</span>
          <span><strong style={{color: balances.retell.ok ? COLORS.success : "var(--textLight)"}} title={balances.retell.error || ""}>{balances.retell.ok ? `${balances.retell.balance.toFixed(2)} ${balances.retell.currency}` : "manuell prüfen"}</strong> Retell {!balances.retell.ok && <a href="https://app.retellai.com" target="_blank" rel="noopener" style={{color:COLORS.primary, fontSize:11}}>(Dashboard)</a>}</span>
          <span style={{color:"var(--textLight)"}}>·</span>
          <span><strong style={{color: balances.stripe.ok ? COLORS.success : "var(--textLight)"}}>{balances.stripe.ok ? `${balances.stripe.balance.toFixed(2)} ${balances.stripe.currency}` : "—"}</strong> Stripe {balances.stripe.ok && balances.stripe.pending > 0 && <span style={{color:"var(--textLight)", fontSize:11}}>(+{balances.stripe.pending.toFixed(2)} pending)</span>}</span>
          {/* ANTHROPIC_BALANCE_v1 -- Monats-Verbrauch (echtes Guthaben nur über Console abrufbar) */}
          {balances.anthropic && balances.anthropic.ok && (<>
            <span style={{color:"var(--textLight)"}}>·</span>
            <span title={`${balances.anthropic.mtd_calls} Chats · ${balances.anthropic.mtd_input_tokens.toLocaleString("de-DE")} in / ${balances.anthropic.mtd_output_tokens.toLocaleString("de-DE")} out Tokens diesen Monat`}>
              <strong style={{color: balances.anthropic.mtd_spend_eur > 50 ? COLORS.warning : COLORS.success}}>
                {balances.anthropic.mtd_spend_eur.toFixed(2)} €
              </strong> Anthropic MTD {" "}
              <a href={balances.anthropic.console_url} target="_blank" rel="noopener" style={{color:COLORS.primary, fontSize:11}}>(Console)</a>
            </span>
          </>)}
        </div>
      )}

      {/* Toolbar: Time-Range + Refresh + CSV */}
      <div style={{display:"flex", flexWrap:"wrap", gap:8, marginBottom:20, alignItems:"center"}}>
        {ranges.map(([k,l]) => (
          <button key={k} onClick={() => setRange(k)} style={{padding:"8px 14px", borderRadius:8, border:"1px solid "+(range===k ? COLORS.primary : "var(--border)"), background: range===k ? COLORS.primary : "var(--card)", color: range===k ? "#fff" : "var(--text)", cursor:"pointer", fontWeight: range===k ? 600 : 400, fontSize:13}}>{l}</button>
        ))}
        <div style={{flex:1}} />
        {syncMsg && <span style={{fontSize:12, color:"var(--textLight)"}}>{syncMsg}</span>}
        <button onClick={refresh} disabled={syncing} style={{padding:"8px 16px", borderRadius:8, border:"none", background:COLORS.primary, color:"#fff", cursor: syncing ? "wait" : "pointer", display:"flex", alignItems:"center", gap:6, fontSize:13}}>
          <RefreshCw size={14} style={{animation: syncing ? "spin 1s linear infinite" : "none"}} />
          {syncing ? "Sync..." : "Refresh"}
        </button>
        <button onClick={exportCsv} style={{padding:"8px 16px", borderRadius:8, border:"1px solid var(--border)", background:"var(--card)", color:"var(--text)", cursor:"pointer", fontSize:13}}>CSV</button>
      </div>

      {/* Plattform-Fixkosten-Block */}
      <div style={{padding:16, background:"var(--card)", borderRadius:12, border:"1px solid var(--border)", marginBottom:16}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12}}>
          <h3 style={{margin:0, fontSize:15}}>Plattform-Fixkosten · {ym}</h3>
          <button onClick={() => setShowPlatformForm(!showPlatformForm)} style={{padding:"4px 10px", borderRadius:4, border:"1px solid var(--border)", background:"var(--cardHover)", color:"var(--text)", cursor:"pointer", fontSize:12}}>{showPlatformForm ? "Schließen" : "Bearbeiten"}</button>
        </div>
        {data.platform_costs.pristine && (
          <div style={{padding:8, background:COLORS.warning+"22", color:COLORS.warning, borderRadius:6, marginBottom:10, fontSize:12}}>
            Für {ym} sind noch keine Plattform-Kosten gepflegt — bitte eintragen.
          </div>
        )}
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap:12}}>
          <div><div style={{fontSize:11, color:"var(--textLight)"}}>Hostinger</div><div style={{fontSize:18, fontWeight:600}}>{fmt(data.platform_costs.hostinger_eur)} €</div></div>
          <div><div style={{fontSize:11, color:"var(--textLight)"}}>UptimeRobot</div><div style={{fontSize:18, fontWeight:600}}>{fmt(data.platform_costs.uptimerobot_eur)} €</div></div>
          <div><div style={{fontSize:11, color:"var(--textLight)"}}>Backblaze</div><div style={{fontSize:18, fontWeight:600}}>{fmt(data.platform_costs.backblaze_eur)} €</div></div>
          <div><div style={{fontSize:11, color:"var(--textLight)"}}>Sonstiges</div><div style={{fontSize:18, fontWeight:600}}>{fmt(data.platform_costs.other_eur)} €</div></div>
          <div><div style={{fontSize:11, color:"var(--textLight)"}}>Σ Plattform</div><div style={{fontSize:18, fontWeight:700, color:COLORS.primary}}>{fmt(data.platform_total_eur)} €</div></div>
          <div><div style={{fontSize:11, color:"var(--textLight)"}}>Anteil/Salon ({data.active_salons} aktiv)</div><div style={{fontSize:18, fontWeight:600}}>{fmt(data.platform_share_per_salon)} €</div></div>
        </div>
        {showPlatformForm && (
          <div style={{marginTop:14, paddingTop:14, borderTop:"1px solid var(--border)", display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:10}}>
            <label><div style={{fontSize:11, color:"var(--textLight)"}}>Hostinger €</div><input type="number" step="0.01" value={platformDraft.hostinger_eur} onChange={e=>setPlatformDraft({...platformDraft, hostinger_eur:parseFloat(e.target.value)||0})} style={{width:"100%", padding:6}} /></label>
            <label><div style={{fontSize:11, color:"var(--textLight)"}}>UptimeRobot €</div><input type="number" step="0.01" value={platformDraft.uptimerobot_eur} onChange={e=>setPlatformDraft({...platformDraft, uptimerobot_eur:parseFloat(e.target.value)||0})} style={{width:"100%", padding:6}} /></label>
            <label><div style={{fontSize:11, color:"var(--textLight)"}}>Backblaze €</div><input type="number" step="0.01" value={platformDraft.backblaze_eur} onChange={e=>setPlatformDraft({...platformDraft, backblaze_eur:parseFloat(e.target.value)||0})} style={{width:"100%", padding:6}} /></label>
            <label><div style={{fontSize:11, color:"var(--textLight)"}}>Sonstiges €</div><input type="number" step="0.01" value={platformDraft.other_eur} onChange={e=>setPlatformDraft({...platformDraft, other_eur:parseFloat(e.target.value)||0})} style={{width:"100%", padding:6}} /></label>
            <label style={{gridColumn:"1 / -1"}}><div style={{fontSize:11, color:"var(--textLight)"}}>Notizen</div><textarea value={platformDraft.notes} onChange={e=>setPlatformDraft({...platformDraft, notes:e.target.value})} rows={2} style={{width:"100%", padding:6}} /></label>
            <button onClick={savePlatform} disabled={savingPlatform} style={{padding:"8px 16px", borderRadius:6, border:"none", background:COLORS.primary, color:"#fff", cursor:"pointer"}}>{savingPlatform ? "Speichere..." : "Speichern"}</button>
          </div>
        )}
      </div>

      {/* Pricing-Empfehlung */}
      <div style={{padding:14, background:"linear-gradient(135deg, "+COLORS.primary+"22, "+COLORS.accent+"22)", border:"1px solid "+COLORS.primary, borderRadius:12, marginBottom:16}}>
        <div style={{fontSize:12, color:"var(--textLight)", marginBottom:4}}>Pricing-Empfehlung</div>
        <div style={{fontSize:17, fontWeight:600}}>
          Bei aktuellem Verbrauch sollte Dein Min-Preis mind. <span style={{color:COLORS.primary}}>{fmt(data.recommendations.suggested_min_price_eur)} €/Monat</span> betragen
        </div>
        <div style={{fontSize:11, color:"var(--textLight)", marginTop:4}}>
          Ø Kosten/Salon: {fmt(data.recommendations.avg_cost_per_salon_eur)} € · Marge-Faktor: {data.recommendations.margin_factor}x ({((data.recommendations.margin_factor-1)*100).toFixed(0)}% Aufschlag)
        </div>
      </div>

      {/* Totals */}
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:10, marginBottom:16}}>
        <StatCard icon={Euro} label={"Einnahmen ("+range+")"} value={fmt(data.totals.revenue_eur)+" €"} color={COLORS.success} />
        <StatCard icon={Bot} label="Retell (Tele)" value={fmt(data.totals.retell_eur)+" €"} color={COLORS.primary} />
        <StatCard icon={MessageSquare} label="Anthropic (KI · MTD)" value={fmt(data.totals.anthropic_eur||0)+" €"} color={COLORS.accent} />
        <StatCard icon={Send} label="Twilio SMS" value={fmt(data.totals.twilio_eur)+" €"} color={COLORS.accent} />
        <StatCard icon={CreditCard} label="Stripe-Fees" value={fmt(data.totals.stripe_fees_eur)+" €"} color={COLORS.warning} />
        <StatCard icon={Server} label="Σ Drittanbieter" value={fmt(data.totals.third_party_eur)+" €"} color={COLORS.danger} />
        <StatCard icon={TrendingUp} label="Σ Marge" value={fmt(data.totals.marge_eur)+" €"} color={data.totals.marge_eur>=0?COLORS.success:COLORS.danger} />
      </div>

      {/* Sort */}
      <div style={{display:"flex", gap:6, marginBottom:8, fontSize:12, alignItems:"center", flexWrap:"wrap"}}>
        <span style={{color:"var(--textLight)"}}>Sortierung:</span>
        {sorts.map(([k,l]) => (
          <button key={k} onClick={()=>setSortBy(k)} style={{padding:"4px 10px", borderRadius:4, border:"1px solid var(--border)", background: sortBy===k ? COLORS.primary : "transparent", color: sortBy===k ? "#fff" : "var(--text)", cursor:"pointer", fontSize:11}}>{l}</button>
        ))}
      </div>

      {/* Salon-Tabelle */}
      <div style={{background:"var(--card)", borderRadius:12, overflow:"auto", border:"1px solid var(--border)"}}>
        <table style={{width:"100%", borderCollapse:"collapse", fontSize:12}}>
          <thead>
            <tr style={{background:"var(--cardHover)", textAlign:"left"}}>
              <th style={{padding:8}}>Salon</th>
              <th style={{padding:8}}>Plan</th>
              <th style={{padding:8, textAlign:"right"}}>Retell</th>
              <th style={{padding:8, textAlign:"right"}}>Claude-Chat</th>
              <th style={{padding:8, textAlign:"right"}}>Twilio</th>
              <th style={{padding:8, textAlign:"right"}}>Stripe-Fee</th>
              <th style={{padding:8, textAlign:"right"}}>Plattform</th>
              <th style={{padding:8, textAlign:"right"}}>Σ Kosten</th>
              <th style={{padding:8, textAlign:"right"}}>Einnahmen</th>
              <th style={{padding:8, textAlign:"right"}}>Marge</th>
              <th style={{padding:8, textAlign:"right"}}>Min-Preis</th>
            </tr>
          </thead>
          <tbody>
            {sortedSalons.map(s => (
              <tr key={s.id} style={{borderTop:"1px solid var(--border)"}}>
                <td style={{padding:8}}><div style={{fontWeight:600}}>{s.name}</div><div style={{fontSize:10, color:"var(--textLight)"}}>{s.city}</div></td>
                <td style={{padding:8}}>{s.plan||"—"}</td>
                <td style={{padding:8, textAlign:"right"}}>{fmt(s.retell_eur)}<div style={{fontSize:10, color:"var(--textLight)"}}>{s.retell_calls}×</div></td>
                <td style={{padding:8, textAlign:"right"}}>{fmt(s.anthropic_eur||0)}<div style={{fontSize:10, color:"var(--textLight)"}}>{s.anthropic_chats||0} chat</div></td>
                <td style={{padding:8, textAlign:"right"}}>{fmt(s.twilio_eur)}<div style={{fontSize:10, color:"var(--textLight)"}}>{s.twilio_count} SMS</div></td>
                <td style={{padding:8, textAlign:"right"}}>{fmt(s.stripe_fees_eur)}</td>
                <td style={{padding:8, textAlign:"right", color:"var(--textLight)"}}>{fmt(s.platform_share_eur)}</td>
                <td style={{padding:8, textAlign:"right", fontWeight:600}}>{fmt(s.total_cost_eur)}</td>
                <td style={{padding:8, textAlign:"right"}}>{fmt(s.revenue_eur)}</td>
                <td style={{padding:8, textAlign:"right", fontWeight:700, color: s.marge_eur>=0 ? COLORS.success : COLORS.danger}}>{fmt(s.marge_eur)}</td>
                <td style={{padding:8, textAlign:"right", color:"var(--textLight)"}}>{fmt(s.breakeven_min_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {sortedSalons.length===0 && <div style={{padding:24, textAlign:"center", color:"var(--textLight)"}}>Keine Salons mit Daten in diesem Zeitraum.</div>}
      </div>

      {data.last_sync && (
        <div style={{marginTop:12, fontSize:11, color:"var(--textLight)", textAlign:"right"}}>
          Letzter Sync: {new Date(data.last_sync.finished_at || data.last_sync.started_at).toLocaleString("de-DE")} · {data.last_sync.twilio} Twilio + {data.last_sync.stripe} Stripe + {data.last_sync.retell} Retell
        </div>
      )}
    </div>
  );
};

export const AdminSalonsPage = () => {
  // PRICING_DROPDOWN_v1 — Plan-Optionen dynamisch aus /api/pricing/plans
  // API_GUARD_v3 -- api.request liefert {ok,status,data} -- .data extrahieren statt direkt-set
  const [pricingPlans, setPricingPlans] = React.useState([]);
  React.useEffect(() => {
    api.request("/pricing/plans").then(r => {
      if (r && r.ok && Array.isArray(r.data)) setPricingPlans(r.data);
      else setPricingPlans([]);
    }).catch(() => setPricingPlans([]));
  }, []);
  const [salons, setSalons] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editingSalonId, setEditingSalonId] = React.useState(null);
    const [showImportModal, setShowImportModal] = React.useState(false);
    const [importSalonId, setImportSalonId] = React.useState(null);
    const [importSalonName, setImportSalonName] = React.useState('');
    const [importStep, setImportStep] = React.useState('upload');
    const [importFile, setImportFile] = React.useState(null);
    const [importHeaders, setImportHeaders] = React.useState([]);
    const [importPreview, setImportPreview] = React.useState([]);
    const [importTotal, setImportTotal] = React.useState(0);
    const [importMapping, setImportMapping] = React.useState({firstname:'',lastname:'',name:'',phone:'',email:'',notes:'',birthday:'',gender:''});
    const [importResult, setImportResult] = React.useState(null);
    const [importLoading, setImportLoading] = React.useState(false);
    const [importError, setImportError] = React.useState('');

    const openImportModal = (salon) => {
      setImportSalonId(salon.id);
      setImportSalonName(salon.name);
      setImportStep('upload');
      setImportFile(null);
      setImportHeaders([]);
      setImportPreview([]);
      setImportMapping({firstname:'',lastname:'',name:'',phone:'',email:'',notes:'',birthday:'',gender:''});
      setImportResult(null);
      setImportError('');
      setShowImportModal(true);
    };

    const handleImportUpload = async () => {
      if (!importFile) return;
      setImportLoading(true);
      setImportError('');
      try {
        const fd = new FormData();
        fd.append('file', importFile);
        const res = await fetch('/api/admin/import-customers/' + importSalonId, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('sp_token') },
          body: fd
        });
        const data = await res.json();
        if (data.preview) {
          setImportHeaders(data.headers);
          setImportPreview(data.rows);
          setImportTotal(data.total);
          // Auto-detect mapping
          const autoMap = {firstname:'',lastname:'',name:'',phone:'',email:'',notes:'',birthday:'',gender:''};
          data.headers.forEach(h => {
            const hl = h.toLowerCase();
            if (!autoMap.firstname && (hl === 'firstname' || hl === 'vorname' || hl === 'first_name')) autoMap.firstname = h;
            if (!autoMap.lastname && (hl === 'lastname' || hl === 'nachname' || hl === 'last_name' || hl === 'familienname')) autoMap.lastname = h;
            if (!autoMap.name && (hl === 'name' || hl === 'fullname' || hl === 'kunde' || hl === 'firstnamelastname' || hl === 'client')) autoMap.name = h;
            if (!autoMap.phone && (hl.includes('phone') || hl.includes('telefon') || hl.includes('tel') || hl.includes('mobil') || hl.includes('handy'))) autoMap.phone = h;
            if (!autoMap.email && (hl.includes('email') || hl.includes('e-mail') || hl.includes('mail'))) autoMap.email = h;
            if (!autoMap.notes && (hl.includes('note') || hl.includes('notiz') || hl.includes('bemerkung') || hl.includes('kommentar') || hl.includes('profil') || hl === 'profiles')) autoMap.notes = h;
            if (!autoMap.birthday && (hl.includes('birthday') || hl.includes('geburtstag') || hl.includes('geb') || hl.includes('birth'))) autoMap.birthday = h;
            if (!autoMap.gender && (hl === 'sex' || hl === 'geschlecht' || hl === 'gender' || hl === 'anrede')) autoMap.gender = h;
          });
          setImportMapping(autoMap);
          setImportStep('mapping');
        } else if (data.error) {
          setImportError(data.error);
        }
      } catch (e) {
        setImportError('Verbindungsfehler: ' + e.message);
      }
      setImportLoading(false);
    };

    const handleImportExecute = async () => {
      if (!importMapping.name && !importMapping.firstname) { setImportError('Bitte mindestens die Spalte "Name" zuordnen'); return; }
      setImportLoading(true);
      setImportError('');
      try {
        const fd = new FormData();
        fd.append('file', importFile);
        const params = new URLSearchParams();
        if (importMapping.name) params.set('map_name', importMapping.name);
        if (importMapping.phone) params.set('map_phone', importMapping.phone);
        if (importMapping.email) params.set('map_email', importMapping.email);
        if (importMapping.notes) params.set('map_notes', importMapping.notes);
        if (importMapping.birthday) params.set('map_birthday', importMapping.birthday);
        if (importMapping.firstname) params.set('map_firstname', importMapping.firstname);
        if (importMapping.lastname) params.set('map_lastname', importMapping.lastname);
        if (importMapping.gender) params.set('map_gender', importMapping.gender);
        params.set('update_mode', 'update');
        const res = await fetch('/api/admin/import-customers/' + importSalonId + '?' + params.toString(), {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('sp_token') },
          body: fd
        });
        const data = await res.json();
        if (data.success) {
          setImportResult(data);
          setImportStep('result');
        } else {
          setImportError(data.error || 'Import fehlgeschlagen');
        }
      } catch (e) {
        setImportError('Verbindungsfehler: ' + e.message);
      }
      setImportLoading(false);
    };
  const [formData, setFormData] = React.useState({
    name: "", city: "", postal_code: "", address: "", phone: "", email: "",
    plan: "starter", free_until: "",
    trial_ends_at: "", // TRIAL_LOCK_v1: Test endet am (nur plan=trial), leer = kein Limit
    /* SALON_FORM_v1: opening_time/closing_time werden NICHT mehr im Form gesetzt — Inhaber pflegt im Schichtplan.
       Backend hat Default. Wenn doch nötig: hier explizit setzen. */
    ai_telephony: false, online_booking: true,
    ai_visibility_enabled: 0, website_url: "", // AI_VISIBILITY_v1
    owner_name: "", owner_email: "", owner_password: "",
    owner_birthday: "", // SALON_FORM_v1: Geburtsdatum optional
    pos_provider: "", // POS_TILLHUB_v1 -- vorausgewählter Provider (Tillhub/Bcassa/...)
    send_setup_mail: true, // POS_TILLHUB_v1 -- automatisch Setup-Mail an Inhaber
    // SMS_ADMIN_CONTROL_v1 -- SMS-Schalter pro Salon (Admin steuert alle 5)
    sms_booking_confirm: true, sms_reminder_24h: true, sms_reminder_2h: true,
    sms_review_request: true, sms_manual_booking: false,
    // SMS_QUOTA_v1 -- Kontingent-Pricing (Default AUS, Aktivierung nur bewusst pro Salon)
    sms_quota_enabled: true, sms_quota_included: 400, sms_overage_cents: 20,
    // POS_SHEARLY_SSO_v1 -- Shearly Kasse (Default AUS, Aktivierung nur bewusst pro Salon)
    pos_shearly_enabled: false,
    // WEBSITE_SUB_v1 -- Webseiten-Abo 24,90 € brutto/Monat (Default AUS, admin-only, Sofort-Aktion)
    website_subscription_enabled: false,
  });
  const [formError, setFormError] = React.useState("");
  const [formSuccess, setFormSuccess] = React.useState(null);

  const loadSalons = async () => {
    try {
      const res = await fetch("/api/admin/salons", { headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") } }).then(r => r.json());
      if (res.salons) setSalons(res.salons);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  React.useEffect(() => { loadSalons(); }, []);

  const handleSubmit = async () => {
    setFormError("");
    setFormSuccess(null);
    if (!formData.name || !formData.owner_name || !formData.owner_email || (!editingSalonId && !formData.owner_password)) {
      return setFormError("Bitte alle Pflichtfelder ausfüllen.");
    }
    if (formData.owner_password && formData.owner_password.length < 8) {
      return setFormError("Passwort muss mindestens 8 Zeichen + 1 Zahl/Sonderzeichen haben.");
    }
    try {
      const res = await fetch(`/api/admin/salons${editingSalonId ? "/" + editingSalonId : ""}`, { method: editingSalonId ? 'PATCH' : 'POST', headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token"), "Content-Type": "application/json" }, body: JSON.stringify(formData) }).then(r => r.json());
      if (res.error) return setFormError(res.error);
      setFormSuccess(res);
      setFormData({ name: "", city: "", postal_code: "", address: "", phone: "", email: "", plan: "starter", free_until: "", trial_ends_at: "", ai_telephony: false, online_booking: true, ai_visibility_enabled: 0, website_url: "", owner_name: "", owner_email: "", owner_password: "", owner_birthday: "", pos_provider: "", send_setup_mail: true, sms_booking_confirm: true, sms_reminder_24h: true, sms_reminder_2h: true, sms_review_request: true, sms_manual_booking: false, sms_quota_enabled: true, sms_quota_included: 400, sms_overage_cents: 20, pos_shearly_enabled: false, website_subscription_enabled: false });
      loadSalons();
            setShowForm(false);
            setEditingSalonId(null);
      } catch (e) { setFormError("Verbindungsfehler"); }
  };

  // WEBSITE_SUB_v1 -- Webseiten-Abo pro Salon an/aus. Sofort-Aktion (nicht via Speichern),
  // weil es echte Stripe-Abrechnung auslöst: bei aktivem Abo wird die Position sofort
  // an-/abgehängt, sonst nur das Flag gesetzt (Abrechnung dann beim nächsten Checkout).
  const toggleWebsiteSub = async (enabled) => {
    if (!editingSalonId) return;
    const msg = enabled
      ? "Webseiten-Abo (24,90 € brutto/Monat) für diesen Salon AKTIVIEREN?\n\nBei einem aktiven Stripe-Abo wird die Position sofort dazugebucht (anteilig). Ohne aktives Abo greift sie beim nächsten Checkout."
      : "Webseiten-Abo für diesen Salon DEAKTIVIEREN?\n\nEine vorhandene Position wird aus dem laufenden Stripe-Abo entfernt.";
    if (!window.confirm(msg)) return;
    setFormError(""); setFormSuccess(null);
    try {
      const res = await fetch(`/api/admin/salons/${editingSalonId}/website-subscription`, {
        method: "POST",
        headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token"), "Content-Type": "application/json" },
        body: JSON.stringify({ enabled })
      }).then(r => r.json());
      if (res.error) return setFormError(res.error);
      setFormData(fd => ({ ...fd, website_subscription_enabled: enabled }));
      setFormSuccess({ message: res.message || (enabled ? "Webseiten-Abo aktiviert." : "Webseiten-Abo deaktiviert.") });
      loadSalons();
    } catch (e) { setFormError("Verbindungsfehler"); }
  };

  const updateSalonStatus = async (id, status) => {
    try {
      await fetch(`/api/admin/salons/${id}`, { method: "PATCH", headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token"), "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
      loadSalons();
    } catch (e) { console.error(e); }
  };

  const inputStyle = { width: "100%", padding: "10px 14px", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", background: "var(--card)" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: COLORS.textLight, marginBottom: 6, marginTop: 14 };

  return (
  <div>
    <Header title="Salons verwalten" subtitle="Alle registrierten Salons — Konfiguration & Zugangsdaten" />

    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
          {salons.length} Salon{salons.length !== 1 ? "s" : ""} registriert
        </h3>
        <Btn size="sm" onClick={() => { setShowForm(!showForm); setFormSuccess(null); setFormError(""); }}>
          <Plus size={12} /> {editingSalonId ? "Salon bearbeiten" : "Neuen Salon anlegen"}
        </Btn>
      </div>

      
    {showImportModal && (
      <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => setShowImportModal(false)}>
        <div style={{background:'white',borderRadius:12,padding:24,maxWidth:700,width:'90%',maxHeight:'80vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}} onClick={e => e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <h3 style={{margin:0}}>Kunden importieren: {importSalonName}</h3>
            <Btn size="sm" variant="ghost" onClick={() => setShowImportModal(false)}>X</Btn>
          </div>

          {importError && <div style={{background:'#fee',color:'#c00',padding:12,borderRadius:8,marginBottom:12}}>{importError}</div>}

          {importStep === 'upload' && (
            <div>
              <p style={{color:'#666',marginBottom:16}}>Laden Sie eine CSV-Datei mit Kundendaten hoch. Die Datei sollte Spaltenüberschriften enthalten (z.B. Name, Telefon, E-Mail).</p>
              <p style={{color:'#888',fontSize:13,marginBottom:16}}>Unterstützte Quellen: Salonkee, Planity, Mitdenkt, Phorest, oder jede andere CSV/Excel-Datei.</p>
              <div style={{border:'2px dashed #ccc',borderRadius:12,padding:32,textAlign:'center',marginBottom:16,background:importFile ? '#f0fff0' : '#fafafa'}}>
                <input type="file" accept=".csv,.txt" onChange={e => setImportFile(e.target.files[0])} style={{marginBottom:8}} />
                {importFile && <p style={{color:'#080',margin:0}}>Datei: {importFile.name} ({Math.round(importFile.size/1024)} KB)</p>}
              </div>
              <Btn onClick={handleImportUpload} disabled={!importFile || importLoading}>{importLoading ? 'Wird analysiert...' : 'Datei analysieren'}</Btn>
            </div>
          )}

          {importStep === 'mapping' && (
            <div>
              <p style={{color:'#666',marginBottom:8}}>{importTotal} Zeilen gefunden. Ordnen Sie die CSV-Spalten den Shearly-Feldern zu:</p>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
                {[
                  {key:'firstname',label:'Vorname'},
                  {key:'lastname',label:'Nachname'},
                  {key:'name',label:'Name (kombiniert)'},
                  {key:'phone',label:'Telefon'},
                  {key:'email',label:'E-Mail'},
                  {key:'notes',label:'Notizen'},
                  {key:'birthday',label:'Geburtstag'},
                  {key:'gender',label:'Geschlecht'}
                ].map(field => (
                  <div key={field.key}>
                    <label style={{fontSize:12,fontWeight:600,color:'#555',textTransform:'uppercase'}}>{field.label}</label>
                    <select value={importMapping[field.key]} onChange={e => setImportMapping(m => ({...m, [field.key]: e.target.value}))} style={{width:'100%',padding:8,borderRadius:6,border:'1px solid #ddd'}}>
                      <option value="">— nicht zuordnen —</option>
                      {importHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              {importPreview.length > 0 && (
                <div style={{marginBottom:16}}>
                  <h4 style={{marginBottom:8}}>Vorschau (erste {importPreview.length} Zeilen):</h4>
                  <div style={{overflow:'auto',maxHeight:200}}>
                    <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
                      <thead><tr>{importHeaders.map(h => <th key={h} style={{padding:'4px 8px',borderBottom:'2px solid #ddd',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>)}</tr></thead>
                      <tbody>{importPreview.map((row,i) => <tr key={i}>{importHeaders.map(h => <td key={h} style={{padding:'4px 8px',borderBottom:'1px solid #eee',whiteSpace:'nowrap'}}>{row[h]}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                </div>
              )}
              <div style={{display:'flex',gap:8}}>
                <Btn onClick={handleImportExecute} disabled={!importMapping.name || importLoading}>{importLoading ? 'Importiere...' : 'Import starten'}</Btn>
                <Btn variant="secondary" onClick={() => { setImportStep('upload'); setImportFile(null); }}>Zurück</Btn>
              </div>
            </div>
          )}

          {importStep === 'result' && importResult && (
            <div>
              <div style={{background:'#f0fff0',border:'1px solid #0a0',borderRadius:8,padding:16,marginBottom:16}}>
                <h4 style={{color:'#080',margin:'0 0 8px'}}>Import abgeschlossen!</h4>
                <p style={{margin:0}}><strong>{importResult.imported}</strong> Kunden neu importiert</p>
              {importResult.updated > 0 && <p style={{margin:0}}><strong>{importResult.updated}</strong> bestehende aktualisiert</p>}
                <p style={{margin:0}}><strong>{importResult.skipped}</strong> übersprungen (Duplikate oder leer)</p>
              </div>
              {importResult.errors && importResult.errors.length > 0 && (
                <div style={{background:'#fff5f5',border:'1px solid #fcc',borderRadius:8,padding:12,marginBottom:16}}>
                  <h5 style={{margin:'0 0 4px',color:'#c00'}}>Fehler:</h5>
                  {importResult.errors.map((e,i) => <p key={i} style={{margin:0,fontSize:12,color:'#800'}}>{e}</p>)}
                </div>
              )}
              <Btn onClick={() => setShowImportModal(false)}>Schließen</Btn>
            </div>
          )}
        </div>
      </div>
    )}
    {showForm && (
        <div style={{ padding: 20, background: "var(--cardHover)", borderRadius: 12, marginBottom: 20, border: `1px solid ${COLORS.border}` }}>
          <h4 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700 }}>{editingSalonId ? "Salon bearbeiten" : "Neuen Salon anlegen"}</h4>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: COLORS.textLight }}>Erstellt den Salon + Login für den Inhaber</p>

          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: "0 20px" }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 4, marginTop: 8 }}>Salon-Daten</div>
              <label style={labelStyle}>Salonname *</label>
              <input style={inputStyle} value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="z.B. Salon Elegance" />
              {/* SALON_FORM_v1: Strasse/Hausnummer einzeln */}
              <label style={labelStyle}>Straße und Hausnummer</label>
              <input style={inputStyle} value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="Musterstraße 1" />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>PLZ</label>
                  <input style={inputStyle} value={formData.postal_code} onChange={e => setFormData({...formData, postal_code: e.target.value.replace(/[^0-9]/g, "").slice(0,5)})} placeholder="80686" maxLength={5} />
                </div>
                <div>
                  <label style={labelStyle}>Stadt</label>
                  <input style={inputStyle} value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} placeholder="München" />
                </div>
              </div>
              <label style={labelStyle}>Telefon</label>
              <input style={inputStyle} value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="+49 89 1234567" />
              <label style={labelStyle}>E-Mail (Salon)</label>
              <input style={inputStyle} value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="info@salon-elegance.de" />
              {/* SALON_FORM_v1: Öffnungszeiten raus — pflegt der Inhaber im Schichtplan, da unterschiedlich pro Wochentag */}
              <div style={{ marginTop: 8, padding: 10, background: "rgba(124,58,237,0.08)", border: "1px solid rgba(124,58,237,0.25)", borderRadius: 6, fontSize: 11.5, color: COLORS.textLight, lineHeight: 1.5 }}>
                💡 Öffnungszeiten pflegt der Inhaber später selbst im <strong>Schichtplan</strong> — variiert pro Wochentag.
              </div>
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 4, marginTop: 8 }}>Inhaber-Login</div>
              <label style={labelStyle}>Inhaber-Name *</label>
              <input style={inputStyle} value={formData.owner_name} onChange={e => setFormData({...formData, owner_name: e.target.value})} placeholder="Max Mustermann" />
              <label style={labelStyle}>Inhaber-E-Mail (= Login) *</label>
              <input style={inputStyle} type="email" value={formData.owner_email} onChange={e => setFormData({...formData, owner_email: e.target.value})} placeholder="max@salon-elegance.de" />
              <label style={labelStyle}>Passwort *</label>
              <input style={inputStyle} type="password" value={formData.owner_password} onChange={e => setFormData({...formData, owner_password: e.target.value})} placeholder="Min. 8 Zeichen + 1 Zahl/Sonderzeichen" />
              {/* SALON_FORM_v1: Geburtsdatum optional · DATE_DMY_INPUT_v1: freies Tipp-Feld TT.MM.JJJJ statt Kalender */}
              <label style={labelStyle}>Geburtsdatum (optional)</label>
              <DateDMYInput style={inputStyle} value={formData.owner_birthday} onChange={v => setFormData({...formData, owner_birthday: v})} placeholder="TT.MM.JJJJ (z. B. 14.05.1990)" />

              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 4, marginTop: 20 }}>Abo & Features</div>
              <label style={labelStyle}>Plan</label>
              <select style={inputStyle} value={formData.plan} onChange={e => {
                const p = e.target.value;
                const inkl = ["business", "premium", "pro"].includes(p.toLowerCase());
                setFormData({...formData, plan: p, ai_visibility_enabled: inkl ? 1 : formData.ai_visibility_enabled});
              }}>
                <option value="free">Kostenlos (dauerhaft)</option>
                <option value="trial">Trial (kostenlos, begrenzt)</option>
                {pricingPlans.filter(p => p.slug !== "free" && p.slug !== "trial").map(p => <option key={p.slug} value={p.slug}>{p.display_name} ({Number(p.monthly_eur).toFixed(0)} €/mtl.)</option>)}
                
                
              </select>
              {formData.plan !== "free" && (
                <>
                  <label style={labelStyle}>Kostenfrei bis (optional)</label>
                  <input style={inputStyle} type="date" value={formData.free_until} onChange={e => setFormData({...formData, free_until: e.target.value})} />
                  {formData.free_until && <div style={{ fontSize: 11, color: COLORS.info, marginTop: 4 }}>Salon zahlt bis {new Date(formData.free_until).toLocaleDateString("de-DE")} nichts. (Nur Info — keine Sperre.)</div>}
                </>
              )}
              {/* TRIAL_LOCK_v1 -- Test-Ende nur bei Trial-Plan: wird ENFORCED (Banner → Kulanz → Sperre) */}
              {(formData.plan || "").toLowerCase() === "trial" && (
                <>
                  <label style={labelStyle}>Test endet am (leer = kein Limit)</label>
                  <input style={inputStyle} type="date" value={formData.trial_ends_at} onChange={e => setFormData({...formData, trial_ends_at: e.target.value})} />
                  <div style={{ fontSize: 11, color: COLORS.warning, marginTop: 4 }}>
                    {formData.trial_ends_at
                      ? <>Ab 7 Tage vorher sieht der Inhaber einen Banner, nach dem {new Date(formData.trial_ends_at).toLocaleDateString("de-DE")} gibt es 3 Tage Kulanz (roter Banner), danach wird der Zugang gesperrt (inkl. Online-Buchung, KI-Telefon, Zoey). Datum verlängern oder leeren hebt alles sofort auf.</>
                      : <>Ohne Datum läuft der Test unbegrenzt — keine Sperre.</>}
                  </div>
                </>
              )}

              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={formData.online_booking} onChange={e => setFormData({...formData, online_booking: e.target.checked})} style={{ accentColor: COLORS.primary }} />
                  Online-Buchung aktivieren
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
                  <input type="checkbox" checked={formData.ai_telephony} onChange={e => setFormData({...formData, ai_telephony: e.target.checked})} style={{ accentColor: COLORS.primary }} />
                  KI-Telefonie (Retell AI) — optional, Aufpreis
                </label>
                {/* SMS_ADMIN_CONTROL_v1 -- SMS-Schalter pro Salon. Inhaber sieht nur die ersten zwei. */}
                <div style={{ marginTop: 6, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 2 }}>SMS-Benachrichtigungen</div>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 10 }}>Bestätigung + 24h-Erinnerung sieht der Inhaber selbst. Die anderen drei steuerst nur du (Admin) — z.B. Google-Bewertung kurz freischalten.</div>
                  {[
                    { key: "sms_booking_confirm", label: "Buchungsbestätigung", admin: false },
                    { key: "sms_reminder_24h", label: "24h-Erinnerung", admin: false },
                    { key: "sms_manual_booking", label: "Manuelle Buchung", admin: true },
                    { key: "sms_reminder_2h", label: "2h-Erinnerung", admin: true },
                    { key: "sms_review_request", label: "Google-Bewertung", admin: true },
                  ].map(t => (
                    <label key={t.key} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer", padding: "4px 0" }}>
                      <input type="checkbox" checked={!!formData[t.key]} onChange={e => setFormData({...formData, [t.key]: e.target.checked})} style={{ accentColor: COLORS.primary }} />
                      <span>{t.label}{t.admin
                        ? <span style={{ color: COLORS.warning, fontWeight: 700, marginLeft: 6, fontSize: 11 }}>nur Admin</span>
                        : <span style={{ color: COLORS.textLight, marginLeft: 6, fontSize: 11 }}>Inhaber sichtbar</span>}</span>
                    </label>
                  ))}
                </div>
                {/* SMS_QUOTA_v1 -- Kontingent-Pricing: Inklusiv-SMS + automatische Mehr-SMS-Rechnung */}
                <div style={{ marginTop: 6, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 2 }}>SMS-Kontingent (Mehr-SMS-Abrechnung)</div>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 10 }}>
                    Wenn aktiv: Inklusiv-SMS pro Monat sind frei, jede weitere SMS wird am Monatsanfang für den Vormonat automatisch per Stripe-Rechnung abgebucht (Preis netto + USt.). Der Aktivierungsmonat ist frei — abgerechnet werden nur volle Monate. Standard seit 01.10.2026 für alle Salons: an, 400 SMS inklusive, 0,20 € netto je Mehr-SMS (AGB § 4). {/* SMS_QUOTA_DEFAULT_v1 */}
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer", padding: "4px 0" }}>
                    <input type="checkbox" checked={!!formData.sms_quota_enabled} onChange={e => setFormData({...formData, sms_quota_enabled: e.target.checked})} style={{ accentColor: COLORS.primary }} />
                    <span>Kontingent aktiv<span style={{ color: COLORS.warning, fontWeight: 700, marginLeft: 6, fontSize: 11 }}>nur Admin</span></span>
                  </label>
                  {!!formData.sms_quota_enabled && (
                    <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ ...labelStyle, marginTop: 0 }}>Inklusiv-SMS/Monat</label>
                        <input type="number" min="0" step="10" style={inputStyle} value={formData.sms_quota_included}
                          onChange={e => setFormData({...formData, sms_quota_included: Math.max(0, parseInt(e.target.value || "0", 10) || 0)})} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ ...labelStyle, marginTop: 0 }}>Mehrpreis (Cent, netto)</label>
                        <input type="number" min="1" step="1" style={inputStyle} value={formData.sms_overage_cents}
                          onChange={e => setFormData({...formData, sms_overage_cents: Math.max(1, parseInt(e.target.value || "1", 10) || 1)})} />
                        <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 4 }}>= {((formData.sms_overage_cents || 0) / 100).toFixed(2)} € netto je Mehr-SMS</div>
                      </div>
                    </div>
                  )}
                </div>
                {/* POS_SHEARLY_SSO_v1 -- Shearly Kasse (eigenes Kassen-Modul) pro Salon freischalten */}
                <div style={{ marginTop: 6, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 2 }}>Shearly Kasse (Kassen-Modul)</div>
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 10 }}>
                    Schaltet den Menüpunkt „Kasse" für Inhaber + Mitarbeiter frei (Login ohne zweites Passwort auf kasse.shearly.pro). <b>Der Salon muss vorher in der Kasse angelegt (onboardet) sein</b>, sonst läuft er dort ins Leere. Fremdkassen-Anbindung (Tillhub &amp; Co.) bleibt unabhängig davon möglich.
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer", padding: "4px 0" }}>
                    <input type="checkbox" checked={!!formData.pos_shearly_enabled} onChange={e => setFormData({...formData, pos_shearly_enabled: e.target.checked})} style={{ accentColor: COLORS.primary }} />
                    <span>Shearly Kasse aktiv<span style={{ color: COLORS.warning, fontWeight: 700, marginLeft: 6, fontSize: 11 }}>nur Admin</span></span>
                  </label>
                </div>
                {/* KASSE_ABO_v1 -- Status der Kassen-Anfrage (Nachweis im Register), nur beim Bearbeiten */}
                {editingSalonId && <AdminKasseBox salonId={editingSalonId} />}
                {/* TERMS_REGISTER_v1 -- Zustimmungs-Nachweis, nur beim Bearbeiten */}
                {editingSalonId && <TermsAcceptancesCard salonId={editingSalonId} />}
                {/* WEBSITE_SUB_v1 -- Webseiten-Abo (24,90 € brutto/Monat) pro Salon. Sofort-Aktion, nur beim Bearbeiten. */}
                {editingSalonId && (
                  <div style={{ marginTop: 6, padding: 12, border: `1px solid ${COLORS.border}`, borderRadius: 8, background: "var(--cardHover)" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 2 }}>Webseiten-Abo</div>
                    <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 10 }}>
                      Monatliches Abo <b>24,90 € brutto</b> (inkl. USt.) für Hosting + Pflege der Salon-Webseite. Wird als eigene Position zusätzlich zur Shearly-Gebühr über Stripe abgerechnet. Die einmalige Einrichtungsgebühr läuft separat (Lexware). <b>Sofort-Aktion</b> — kein „Speichern" nötig.
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer", padding: "4px 0" }}>
                      <input type="checkbox" checked={!!formData.website_subscription_enabled} onChange={e => toggleWebsiteSub(e.target.checked)} style={{ accentColor: COLORS.primary }} />
                      <span>Webseiten-Abo aktiv<span style={{ color: COLORS.warning, fontWeight: 700, marginLeft: 6, fontSize: 11 }}>nur Admin · 24,90 €</span></span>
                    </label>
                  </div>
                )}
                {/* RETELL_PROVISION_v1 — Self-Service-Setup: nur beim Bearbeiten existierender Salons */}
                {editingSalonId && <RetellSetupCard salonId={editingSalonId} salonName={formData.name} />}
                {/* AI_VISIBILITY_v1 — Plan-aware Toggle (Business/Premium = inklusive, Starter = Add-on) */}
                {(() => {
                  const planLower = (formData.plan || "").toLowerCase();
                  const inkl = ["business", "premium", "pro"].includes(planLower);
                  return (
                    <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={!!formData.ai_visibility_enabled} onChange={e => setFormData({...formData, ai_visibility_enabled: e.target.checked ? 1 : 0})} style={{ accentColor: COLORS.primary }} />
                      <span>
                        KI-Sichtbarkeit (ChatGPT/Perplexity/Google AI)
                        {inkl
                          ? <span style={{ color: COLORS.success, fontWeight: 700, marginLeft: 6 }}>— im {planLower === "premium" ? "Premium" : (planLower === "pro" ? "Pro" : "Business")}-Plan inklusive</span>
                          : <span style={{ color: COLORS.textLight, marginLeft: 6 }}>— Add-on, 9 €/Mt Launch-Preis (bis 30.06.2026, danach 19 €)</span>
                        }
                      </span>
                    </label>
                  );
                })()}
                {!!formData.ai_visibility_enabled && (
                  <div style={{
                    fontSize: 11, color: COLORS.textLight, lineHeight: 1.55,
                    background: COLORS.primary + "0d", padding: 10, borderRadius: 6,
                    borderLeft: "3px solid " + COLORS.primary, marginLeft: 26
                  }}>
                    <b style={{ color: COLORS.text }}>Was passiert nach Aktivierung:</b> Owner sieht ab sofort die Premium-Card im Dashboard mit Score, Embed-Code und „An Webentwickler senden"-Button. Damit der Score gemessen werden kann, braucht der Salon eine Website-URL (siehe Feld unten — kann auch der Owner selbst nachtragen). Score 5/5 erreicht der Salon erst, wenn der Webentwickler den Code-Snippet einmal in den &lt;head&gt; der Website einbaut.
                  </div>
                )}
              </div>
              <label style={{...labelStyle, marginTop: 12}}>Salon-Website (fuer KI-Sichtbarkeit, optional)</label>
              <input style={inputStyle} type="url" placeholder="https://www.dein-salon.de" value={formData.website_url || ""} onChange={e => setFormData({...formData, website_url: e.target.value})} />
              {!!formData.ai_visibility_enabled && !formData.website_url && (
                <div style={{ fontSize: 11, color: COLORS.warning, marginTop: 4 }}>
                  ⚠ Ohne URL kein Sichtbarkeits-Check möglich.
                </div>
              )}

              {/* POS_TILLHUB_v1 -- Kassensystem-Voreinstellung + Setup-Mail */}
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 4, marginTop: 20 }}>Onboarding</div>
              <label style={labelStyle}>Kassensystem-Provider (optional, Inhaber trägt API-Key selbst nach)</label>
              <select style={inputStyle} value={formData.pos_provider || ""} onChange={e => setFormData({...formData, pos_provider: e.target.value})}>
                <option value="">— keine Voreinstellung —</option>
                <option value="sumup">SumUp (für Solo/Kleinst-Salons — gratis App + günstiger Reader)</option>
                <option value="tillhub">Tillhub (Profi-Setup, mittlere bis große Salons)</option>
                <option value="bcassa">Bcassa (Boutique/Beauty-Brand-Fokus)</option>
                <option value="lightspeed">Lightspeed (für Salons mit Online-Shop, in Vorbereitung)</option>
                <option value="ready2order">ready2order (in Vorbereitung)</option>
                <option value="helcim">Helcim (in Vorbereitung)</option>
              </select>
              <div style={{
                fontSize: 11, color: COLORS.textLight, margin: "6px 0 0", lineHeight: 1.55,
                background: "var(--cardHover)", borderLeft: "2px solid " + COLORS.info,
                padding: "8px 10px", borderRadius: 4,
              }}>
                <b style={{ color: COLORS.text }}>Empfehlung für den Salon:</b> Shearly hat bewusst keine eigene Kasse —
                wir integrieren die Marktführer. Alle Optionen sind GoBD- und TSE-konform.
                Verantwortung für Kassen-Compliance liegt beim Salon (siehe AVV § 8).
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, cursor: "pointer", marginTop: 14 }}>
                <input type="checkbox" checked={formData.send_setup_mail !== false} onChange={e => setFormData({...formData, send_setup_mail: e.target.checked})} style={{ accentColor: COLORS.primary }} />
                <span>
                  Setup-Mail an Inhaber senden
                  <span style={{ fontSize: 11, color: COLORS.textLight, display: "block", marginTop: 2 }}>
                    Login-Daten + Schritt-für-Schritt-Anleitung (Kassensystem, Google-Profil, SMS-Signatur)
                  </span>
                </span>
              </label>
            </div>
          </div>

          {formError && <div style={{ marginTop: 14, padding: "10px 14px", background: "rgba(239,68,68,0.15)", border: "1px solid #FECACA", borderRadius: 8, fontSize: 12, color: "#DC2626", fontWeight: 600 }}>{formError}</div>}

          {/* SALON_SUBMIT_FIX_v1: PATCH-Response hat kein login/booking_url -- guarden gegen TypeError */}
          {formSuccess && (
            <div style={{ marginTop: 14, padding: 16, background: "rgba(16,185,129,0.15)", border: "1px solid #A7F3D0", borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#059669", marginBottom: 8 }}>✓ {formSuccess.message || "Gespeichert"}</div>
              {formSuccess.login && (
                <div style={{ lineHeight: 2 }}>
                  <strong>Login:</strong> {formSuccess.login.email} / {formSuccess.login.password}<br/>
                  {formSuccess.booking_url && (
                    <><strong>Buchungsseite:</strong> <a href={formSuccess.booking_url} target="_blank" style={{ color: COLORS.primary }}>shearly.pro{formSuccess.booking_url}</a></>
                  )}
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <Btn onClick={handleSubmit}>{editingSalonId ? "Speichern" : "Salon anlegen"}</Btn>
            <Btn variant="secondary" onClick={() => { setShowForm(false); setEditingSalonId(null); }}>Abbrechen</Btn>
          </div>
        </div>
      )}

      {loading ? <p style={{ fontSize: 13, color: COLORS.textLight }}>Lade Salons...</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
              {["Salon", "Stadt", "Plan", "Status", "Inhaber", "Online-Buchung", "KI-Telefonie", "SMS", "KI-Sichtbarkeit", "Aktionen"].map(h => (
                <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {salons.map(s => (
              <tr key={s.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: "10px", fontWeight: 600 }}>{s.name}<br/><span style={{ fontWeight: 400, color: COLORS.textLight, fontSize: 11 }}>/b/{s.slug}</span></td>
                <td style={{ padding: "10px" }}>{s.city}</td>
                <td style={{ padding: "10px" }}>
                  <Badge text={s.plan === "free" ? "Kostenlos" : s.plan} color={s.plan === "premium" ? "#D97706" : s.plan === "pro" ? COLORS.primary : s.plan === "free" ? COLORS.success : COLORS.textLight} />
                  {s.free_until && <div style={{ fontSize: 10, color: COLORS.info, marginTop: 2 }}>frei bis {new Date(s.free_until).toLocaleDateString("de-DE")}</div>}
                  {/* TRIAL_LOCK_v1 -- Test-Ende anzeigen, rot wenn abgelaufen */}
                  {s.plan === "trial" && s.trial_ends_at && (
                    <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2,
                      color: new Date(s.trial_ends_at + "T23:59:59") < new Date() ? COLORS.danger : COLORS.warning }}>
                      Test bis {new Date(s.trial_ends_at).toLocaleDateString("de-DE")}
                    </div>
                  )}
                </td>
                <td style={{ padding: "10px" }}><Badge text={s.status === "active" ? "Aktiv" : s.status === "trial" ? "Trial" : s.status === "cancelled" ? "Gekündigt" : s.status} color={s.status === "active" ? COLORS.success : s.status === "trial" ? COLORS.warning : COLORS.danger} /></td>
                <td style={{ padding: "10px" }}>{s.owner_name}<br/><span style={{ color: COLORS.textLight, fontSize: 11 }}>{s.owner_email}</span></td>
                <td style={{ padding: "10px" }}>{s.online_booking ? <Badge text="Aktiv" color={COLORS.success} /> : <Badge text="Aus" color={COLORS.textLight} />}</td>
                <td style={{ padding: "10px" }}>{s.ai_telephony ? <Badge text="Aktiv" color={COLORS.primary} /> : <Badge text="—" color={COLORS.textLight} />}</td>
                {/* SMS_ADMIN_CONTROL_v1 -- SMS-Uebersicht (#1): kompakte Kuerzel, gruen=an, grau=aus */}
                <td style={{ padding: "10px" }}>
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {[
                      { key: "sms_booking_confirm", abk: "Best.", title: "Buchungsbestätigung" },
                      { key: "sms_reminder_24h", abk: "24h", title: "24h-Erinnerung" },
                      { key: "sms_manual_booking", abk: "Man.", title: "Manuelle Buchung (nur Admin)" },
                      { key: "sms_reminder_2h", abk: "2h", title: "2h-Erinnerung (nur Admin)" },
                      { key: "sms_review_request", abk: "Bew.", title: "Google-Bewertung (nur Admin)" },
                    ].map(t => (
                      <span key={t.key} title={t.title + ": " + (s[t.key] ? "an" : "aus")} style={{ fontSize: 10, fontWeight: 700, padding: "2px 5px", borderRadius: 5, whiteSpace: "nowrap", color: s[t.key] ? "#15803d" : COLORS.textLight, background: s[t.key] ? "rgba(34,197,94,0.14)" : "var(--cardHover)", border: "1px solid " + (s[t.key] ? "rgba(34,197,94,0.35)" : COLORS.border) }}>{t.abk}</span>
                    ))}
                  </div>
                </td>
                {/* AI_VISIBILITY_v1 -- Add-on-Status + Score */}
                <td style={{ padding: "10px" }}>
                  {s.ai_visibility_enabled
                    ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <Badge text={`Aktiv${typeof s.ai_visibility_score === "number" ? ` · ${s.ai_visibility_score}/5` : ""}`} color={COLORS.primary} />
                        {!s.website_url && <span style={{ fontSize: 10, color: COLORS.warning }}>⚠ keine Website</span>}
                      </div>
                    )
                    : <Badge text="—" color={COLORS.textLight} />
                  }
                </td>
                <td style={{ padding: "10px" }}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Btn size="sm" variant="ghost" title="CSV Import" onClick={(e) => { e.stopPropagation(); openImportModal(s); }}><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></Btn>
                    <Btn size="sm" variant="ghost" onClick={() => { setEditingSalonId(s.id); setFormData({ name: s.name || "", city: s.city || "", address: s.address || "", phone: s.phone || "", email: s.email || "", plan: s.plan || "starter", free_until: s.free_until || "", trial_ends_at: s.trial_ends_at || "", opening_time: s.opening_time || "08:00", closing_time: s.closing_time || "20:00", ai_telephony: !!s.ai_telephony, online_booking: s.online_booking !== false, ai_visibility_enabled: s.ai_visibility_enabled ? 1 : 0, website_url: s.website_url || "", owner_name: s.owner_name || "", owner_email: s.owner_email || "", owner_password: "", pos_provider: s.pos_provider || "", send_setup_mail: false, sms_booking_confirm: s.sms_booking_confirm !== false, sms_reminder_24h: s.sms_reminder_24h !== false, sms_reminder_2h: s.sms_reminder_2h !== false, sms_review_request: s.sms_review_request !== false, sms_manual_booking: !!s.sms_manual_booking, sms_quota_enabled: !!s.sms_quota_enabled, sms_quota_included: (s.sms_quota_included == null ? 400 : s.sms_quota_included), sms_overage_cents: (s.sms_overage_cents == null ? 20 : s.sms_overage_cents), pos_shearly_enabled: !!s.pos_shearly_enabled, website_subscription_enabled: !!s.website_subscription_enabled }); setShowForm(true); setFormError(""); setFormSuccess(null); }} title="Bearbeiten"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg></Btn>
                    {s.status === "active" && <Btn size="sm" variant="secondary" onClick={() => updateSalonStatus(s.id, "suspended")} title="Pausieren"><XCircle size={10} /></Btn>}
                    {s.status === "suspended" && <Btn size="sm" onClick={() => updateSalonStatus(s.id, "active")} title="Aktivieren"><Check size={10} /></Btn>}
                    {/* POS_TILLHUB_v1 -- Setup-Mail erneut senden */}
                    {s.owner_email && (
                      <Btn size="sm" variant="ghost" title={`Setup-Mail an ${s.owner_email} senden`}
                        onClick={async () => {
                          if (!confirm(`Setup-Mail an ${s.owner_email} (Inhaber von ${s.name}) erneut senden?`)) return;
                          const r = await fetch(`/api/admin/salons/${s.id}/resend-setup-mail`, {
                            method: "POST",
                            headers: { "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
                          });
                          const d = await r.json().catch(() => ({}));
                          alert(r.ok ? (d.message || "Mail gesendet ✓") : ("Fehler: " + (d.error || r.status)));
                        }}>
                        <Mail size={11} />
                      </Btn>
                    )}
                    {s.status !== "cancelled" && <Btn size="sm" variant="secondary" onClick={() => { if(confirm("Salon wirklich deaktivieren?")) updateSalonStatus(s.id, "cancelled"); }} title="Kündigen"><Trash2 size={10} /></Btn>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  </div>
  );
};

// FRONTEND_ERROR_LOG_v1 -- Admin Page fuer Frontend-Errors aus Browsern.
export const AdminFrontendErrorsPage = () => {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [unresolvedOnly, setUnresolvedOnly] = React.useState(true);
  const [sinceDays, setSinceDays] = React.useState(7);
  const [selected, setSelected] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({
      limit: 200,
      offset: 0,
      unresolved_only: unresolvedOnly ? "1" : "0",
      since_days: String(sinceDays),
    });
    api.request("/admin/frontend-errors?" + q.toString()).then(r => {
      if (r.ok) setData(r.data);
      setLoading(false);
    });
  }, [unresolvedOnly, sinceDays]);

  React.useEffect(() => { load(); }, [load]);

  const markResolved = async (id) => {
    const r = await api.request("/admin/frontend-errors/" + id + "/resolve", { method: "PATCH" });
    if (r.ok) load();
  };
  const deleteOne = async (id) => {
    if (!confirm("Diesen Eintrag wirklich loeschen?")) return;
    const r = await api.request("/admin/frontend-errors/" + id, { method: "DELETE" });
    if (r.ok) { setSelected(null); load(); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleString("de-DE") : "-";

  return (
    <div>
      <Header title="Frontend-Errors" subtitle="JS-Crashes aus Browsern (FRONTEND_ERROR_LOG_v1)" />

      <div style={{ display: "flex", gap: 12, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <Btn size="sm" variant="secondary" onClick={load}><RefreshCw size={14} /> Neu laden</Btn>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={unresolvedOnly} onChange={e => setUnresolvedOnly(e.target.checked)} style={{ accentColor: COLORS.primary }} />
          nur ungelöste
        </label>
        <label style={{ fontSize: 13 }}>
          Zeitraum:
          <select value={sinceDays} onChange={e => setSinceDays(Number(e.target.value))}
            style={{ marginLeft: 6, padding: "4px 8px", border: `1px solid ${COLORS.border}`, borderRadius: 6, background: "var(--card)", color: COLORS.text }}>
            <option value={1}>letzter Tag</option>
            <option value={7}>letzte 7 Tage</option>
            <option value={30}>letzte 30 Tage</option>
            <option value={90}>letzte 90 Tage</option>
          </select>
        </label>
        {data && (
          <div style={{ marginLeft: "auto", fontSize: 12, color: COLORS.textLight }}>
            Logger: <strong style={{ color: data.logger_enabled ? "#10B981" : "#EF4444" }}>{data.logger_enabled ? "AN" : "AUS"}</strong> · Total {data.total} · Ungelöst (7d) {data.unresolved_last_7d}
          </div>
        )}
      </div>

      {loading && <div style={{ padding: 30, textAlign: "center", color: "var(--textLight)" }}>Lade…</div>}

      {!loading && data && data.items.length === 0 && (
        <Card><div style={{ padding: 30, textAlign: "center", color: "var(--textLight)" }}>
          <CheckCircle size={32} color="#10B981" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Keine Errors im gewählten Zeitraum.</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Alles ruhig — entweder gut oder Logger aus.</div>
        </div></Card>
      )}

      {!loading && data && data.items.length > 0 && (
        <Card style={{ padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--cardHover)", textAlign: "left" }}>
                <th style={{ padding: 8 }}>Zeit</th>
                <th style={{ padding: 8 }}>Salon</th>
                <th style={{ padding: 8 }}>URL</th>
                <th style={{ padding: 8 }}>Message</th>
                <th style={{ padding: 8 }}>Quelle</th>
                <th style={{ padding: 8, textAlign: "center" }}>Status</th>
                <th style={{ padding: 8, textAlign: "right" }}>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(it => (
                <tr key={it.id} style={{ borderTop: `1px solid ${COLORS.border}`, background: it.resolved ? "transparent" : "rgba(239,68,68,0.05)" }}>
                  <td style={{ padding: 8, color: "var(--textLight)", whiteSpace: "nowrap" }}>{fmtDate(it.created_at)}</td>
                  <td style={{ padding: 8 }}>{it.salon_name || (it.salon_id ? "#" + it.salon_id : "-")}<div style={{ fontSize: 10, color: "var(--textLight)" }}>{it.user_role || "anon"}</div></td>
                  <td style={{ padding: 8, fontFamily: "monospace", fontSize: 11, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.url}</td>
                  <td style={{ padding: 8, fontWeight: 500, maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.message}</td>
                  <td style={{ padding: 8, color: "var(--textLight)", fontSize: 11 }}>{it.source}</td>
                  <td style={{ padding: 8, textAlign: "center" }}>
                    <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: it.resolved ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", color: it.resolved ? "#10B981" : "#EF4444" }}>
                      {it.resolved ? "✓ gesehen" : "neu"}
                    </span>
                  </td>
                  <td style={{ padding: 8, textAlign: "right" }}>
                    <button onClick={() => setSelected(it)} style={{ padding: "4px 8px", fontSize: 11, border: `1px solid ${COLORS.border}`, borderRadius: 6, background: "transparent", color: COLORS.text, cursor: "pointer", marginRight: 4 }}>Details</button>
                    {!it.resolved && (
                      <button onClick={() => markResolved(it.id)} style={{ padding: "4px 8px", fontSize: 11, border: `1px solid ${COLORS.success}`, color: COLORS.success, borderRadius: 6, background: "transparent", cursor: "pointer", marginRight: 4 }}>✓</button>
                    )}
                    <button onClick={() => deleteOne(it.id)} style={{ padding: "4px 8px", fontSize: 11, border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: 6, background: "transparent", cursor: "pointer" }}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Frontend-Error Detail" width={780}>
        {selected && (
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "4px 12px", marginBottom: 16 }}>
              <strong>Zeit:</strong><span>{fmtDate(selected.created_at)}</span>
              <strong>Salon:</strong><span>{selected.salon_name || "-"} (id: {selected.salon_id || "-"})</span>
              <strong>User:</strong><span>{selected.user_id ? "#" + selected.user_id : "anonym (vor Login)"} · {selected.user_role || "-"}</span>
              <strong>URL:</strong><span style={{ fontFamily: "monospace", fontSize: 12 }}>{selected.url}</span>
              <strong>Quelle:</strong><span>{selected.source}</span>
              <strong>Browser:</strong><span style={{ fontSize: 11, color: "var(--textLight)" }}>{selected.user_agent}</span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <strong style={{ display: "block", marginBottom: 4, color: COLORS.danger }}>Message</strong>
              <div style={{ padding: 10, background: "rgba(239,68,68,0.08)", border: `1px solid rgba(239,68,68,0.3)`, borderRadius: 6, fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap" }}>{selected.message}</div>
            </div>
            {selected.stack && (
              <div>
                <strong style={{ display: "block", marginBottom: 4 }}>Stack-Trace</strong>
                <pre style={{ padding: 10, background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontFamily: "monospace", fontSize: 11, lineHeight: 1.4, maxHeight: 400, overflow: "auto", whiteSpace: "pre-wrap" }}>{selected.stack}</pre>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "flex-end" }}>
              {!selected.resolved && <Btn variant="success" onClick={() => { markResolved(selected.id); setSelected(null); }}><Check size={14} /> Als gesehen markieren</Btn>}
              <Btn variant="danger" onClick={() => deleteOne(selected.id)}><Trash2 size={14} /> Loeschen</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Admin: Updates & Feature Rollout
/* AUDIT_LOG_UI_v1 + AUDIT_LOG_UI_DARK_v1 -- Admin Audit-Log Page (theme-aware) */
export const AdminAuditPage = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFilters] = useState({ action: "", action_prefix: "", salon_id: "", user_id: "", entity_type: "" });
  const [detail, setDetail] = useState(null);
  const token = sessionStorage.getItem("sp_token");

  const buildQS = (cursor) => {
    const p = new URLSearchParams();
    if (filters.action) p.set("action", filters.action);
    if (filters.action_prefix) p.set("action_prefix", filters.action_prefix);
    if (filters.salon_id) p.set("salon_id", filters.salon_id);
    if (filters.user_id) p.set("user_id", filters.user_id);
    if (filters.entity_type) p.set("entity_type", filters.entity_type);
    if (cursor) p.set("cursor", cursor);
    p.set("limit", "50");
    return p.toString();
  };

  const loadFirst = async () => {
    setLoading(true);
    try {
      const [itRes, stRes] = await Promise.all([
        fetch("/api/admin/audit?" + buildQS(null), { headers: { Authorization: "Bearer " + token } }),
        fetch("/api/admin/audit/stats?days=30", { headers: { Authorization: "Bearer " + token } }),
      ]);
      const it = await itRes.json();
      setItems(it.items || []);
      setNextCursor(it.next_cursor || null);
      setStats(await stRes.json());
    } catch (e) { console.error("audit load:", e); }
    setLoading(false);
  };

  const loadMore = async () => {
    if (!nextCursor) return;
    try {
      const r = await fetch("/api/admin/audit?" + buildQS(nextCursor), { headers: { Authorization: "Bearer " + token } });
      const j = await r.json();
      setItems((cur) => cur.concat(j.items || []));
      setNextCursor(j.next_cursor || null);
    } catch (e) { console.error("audit more:", e); }
  };

  useEffect(() => { loadFirst(); }, []);

  const fmtTs = (s) => {
    if (!s) return "";
    try { const d = new Date(s); return d.toLocaleString("de-DE"); } catch { return s; }
  };

  /* Action-Badges -- theme-aware: dunkler Background-Tint mit kraeftiger Farbe */
  const actionStyle = (a) => {
    if (!a) return { background: "rgba(156,163,175,0.15)", color: "var(--textLight)" };
    if (a.startsWith("auth.login_fail")) return { background: "rgba(239,68,68,0.18)", color: "#f87171" };
    if (a.startsWith("auth.")) return { background: "rgba(59,130,246,0.18)", color: "#60a5fa" };
    if (a.endsWith(".delete")) return { background: "rgba(245,158,11,0.18)", color: "#fbbf24" };
    if (a.endsWith(".create")) return { background: "rgba(16,185,129,0.18)", color: "#34d399" };
    if (a.endsWith(".update")) return { background: "rgba(99,102,241,0.18)", color: "#a5b4fc" };
    return { background: "rgba(156,163,175,0.15)", color: "var(--textLight)" };
  };

  const inputStyle = { padding: 8, background: "var(--card)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6 };
  const cardStyle = { background: "var(--card)", borderRadius: 8, border: "1px solid var(--border)" };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "var(--text)" }}>Lade Audit-Log...</div>;

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", color: "var(--text)" }}>
      {/* HEADER_ALL_PAGES_v1 (M2): Standard-Header mit Menü-Taste statt nacktem h1 */}
      <Header title="Audit-Log" subtitle="Plattform-Audit-Trail — wer hat wann was geändert" />
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 20 }}>
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={{ fontSize: 12, color: "var(--textLight)" }}>Eintraege (letzte {stats.days} Tage)</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{stats.total}</div>
          </div>
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={{ fontSize: 12, color: "var(--textLight)" }}>Login-Fehlversuche (24h)</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: stats.login_fail_24h > 5 ? "#f87171" : "var(--text)" }}>{stats.login_fail_24h}</div>
          </div>
          <div style={{ ...cardStyle, padding: 16 }}>
            <div style={{ fontSize: 12, color: "var(--textLight)" }}>Top-Aktion</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{(stats.by_action && stats.by_action[0]) ? stats.by_action[0].action + " (" + stats.by_action[0].count + ")" : "-"}</div>
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input placeholder="Action exakt" value={filters.action} onChange={e => setFilters({...filters, action: e.target.value})} style={{ ...inputStyle, width: 160 }} />
        <input placeholder="Action-Praefix (z.B. customer.)" value={filters.action_prefix} onChange={e => setFilters({...filters, action_prefix: e.target.value})} style={{ ...inputStyle, width: 220 }} />
        <input placeholder="Salon-ID" value={filters.salon_id} onChange={e => setFilters({...filters, salon_id: e.target.value})} style={{ ...inputStyle, width: 100 }} />
        <input placeholder="User-ID" value={filters.user_id} onChange={e => setFilters({...filters, user_id: e.target.value})} style={{ ...inputStyle, width: 100 }} />
        <input placeholder="Entity-Typ (customer/...)" value={filters.entity_type} onChange={e => setFilters({...filters, entity_type: e.target.value})} style={{ ...inputStyle, width: 200 }} />
        <button onClick={loadFirst} style={{ padding: "8px 16px", background: "var(--primary)", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Filtern</button>
        <button onClick={() => { setFilters({ action: "", action_prefix: "", salon_id: "", user_id: "", entity_type: "" }); }} style={{ padding: "8px 16px", background: "transparent", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer" }}>Reset</button>
      </div>
      <div style={{ ...cardStyle, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--cardHover)", borderBottom: "1px solid var(--border)" }}>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>Zeit</th>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>User</th>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>Rolle</th>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>Action</th>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>Entitaet</th>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>Salon</th>
              <th style={{ padding: 10, textAlign: "left", color: "var(--textLight)", fontWeight: 600 }}>IP</th>
              <th style={{ padding: 10, textAlign: "left" }}></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (<tr><td colSpan="8" style={{ padding: 20, textAlign: "center", color: "var(--textLight)" }}>Keine Eintraege.</td></tr>)}
            {items.map(it => (
              <tr key={it.id} style={{ borderBottom: "1px solid var(--border)" }}>
                <td style={{ padding: 8, whiteSpace: "nowrap", color: "var(--textLight)", fontFamily: "monospace" }}>{fmtTs(it.created_at)}</td>
                <td style={{ padding: 8 }}>{it.user_name || (it.user_id ? "#" + it.user_id : "-")}</td>
                <td style={{ padding: 8, color: "var(--textLight)" }}>{it.user_role || "-"}</td>
                <td style={{ padding: 8 }}><span style={{ ...actionStyle(it.action), padding: "2px 8px", borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{it.action}</span></td>
                <td style={{ padding: 8, color: "var(--textLight)" }}>{it.entity_type ? (it.entity_type + (it.entity_id ? (" #" + it.entity_id) : "")) : "-"}</td>
                <td style={{ padding: 8, color: "var(--textLight)" }}>{it.salon_name || (it.salon_id ? ("#" + it.salon_id) : "-")}</td>
                <td style={{ padding: 8, color: "var(--textLight)", fontFamily: "monospace", fontSize: 11 }}>{it.ip_address || "-"}</td>
                <td style={{ padding: 8 }}><button onClick={() => setDetail(it)} style={{ padding: "4px 10px", background: "transparent", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>Details</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {nextCursor && (
        <div style={{ marginTop: 16, textAlign: "center" }}>
          <button onClick={loadMore} style={{ padding: "8px 24px", background: "transparent", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer" }}>Aeltere laden</button>
        </div>
      )}
      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...cardStyle, padding: 24, maxWidth: 700, width: "100%", maxHeight: "80vh", overflow: "auto" }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Audit-Eintrag #{detail.id}</h2>
            <div style={{ fontSize: 13, color: "var(--text)", marginBottom: 12, lineHeight: 1.7 }}>
              <div><strong style={{ color: "var(--textLight)" }}>Zeit:</strong> {fmtTs(detail.created_at)}</div>
              <div><strong style={{ color: "var(--textLight)" }}>Action:</strong> {detail.action}</div>
              <div><strong style={{ color: "var(--textLight)" }}>User:</strong> {detail.user_name || (detail.user_id ? "#"+detail.user_id : "-")} ({detail.user_role || "?"})</div>
              <div><strong style={{ color: "var(--textLight)" }}>Salon:</strong> {detail.salon_name || (detail.salon_id ? "#"+detail.salon_id : "-")}</div>
              <div><strong style={{ color: "var(--textLight)" }}>Entity:</strong> {detail.entity_type || "-"} {detail.entity_id ? "#"+detail.entity_id : ""}</div>
              <div><strong style={{ color: "var(--textLight)" }}>IP:</strong> {detail.ip_address || "-"}</div>
              <div style={{ wordBreak: "break-all" }}><strong style={{ color: "var(--textLight)" }}>UA:</strong> {detail.user_agent || "-"}</div>
            </div>
            <div style={{ marginBottom: 8, fontSize: 12, color: "var(--textLight)", fontWeight: 600 }}>Payload:</div>
            <pre style={{ background: "var(--cardHover)", color: "var(--text)", padding: 12, borderRadius: 4, fontSize: 12, overflow: "auto", maxHeight: 300, border: "1px solid var(--border)" }}>{JSON.stringify(detail.payload, null, 2) || "null"}</pre>
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button onClick={() => setDetail(null)} style={{ padding: "8px 16px", background: "var(--primary)", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Schliessen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ONBOARDING_VIEW_v1 -- Admin: Einrichtungsfortschritt je Salon (Toby 03.09.2026).
// Datenquelle GET /api/admin/onboarding (nur lesend). Trials mit den wenigsten Resttagen zuerst.
export const AdminOnboardingPage = () => {
  const navigate = React.useContext(NavigateContext);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [onlyTrials, setOnlyTrials] = React.useState(true);
  const [openId, setOpenId] = React.useState(null);
  React.useEffect(() => {
    api.request("/admin/onboarding").then(r => {
      if (r && r.ok) setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);
  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };
  const daysAgo = (iso) => {
    if (!iso) return null;
    const d = new Date(iso); if (isNaN(d)) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  };
  const all = (data && data.salons) || [];
  const rows = all.filter(s => !s.is_test_salon && (!onlyTrials || (s.plan || "").toLowerCase() === "trial"));
  const progressColor = (done, total) => done >= total ? COLORS.success : done >= total / 2 ? COLORS.warning : COLORS.danger;
  const trialLabel = (s) => {
    if ((s.plan || "").toLowerCase() !== "trial") return <Badge text={s.plan || "—"} color={COLORS.primary} />;
    if (s.trial_days_left === null || s.trial_days_left === undefined) return <Badge text="Trial" color={COLORS.textLight} />;
    if (s.trial_days_left < 0) return <Badge text={`Trial abgelaufen (${fmtDate(s.trial_ends_at)})`} color={COLORS.danger} />;
    const c = s.trial_days_left <= 7 ? COLORS.danger : s.trial_days_left <= 14 ? COLORS.warning : COLORS.success;
    return <Badge text={`Trial bis ${fmtDate(s.trial_ends_at)} · ${s.trial_days_left} Tage`} color={c} />;
  };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Onboarding-Fortschritt</h2>
          <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>
            Wer braucht Hilfe? Acht Schritte je Salon, Trials mit den wenigsten Resttagen zuerst. Testsalon 1 ist ausgeblendet.
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={onlyTrials} onChange={e => setOnlyTrials(e.target.checked)} style={{ accentColor: COLORS.primary }} />
          nur Trial-Salons
        </label>
      </div>
      {loading && <div style={{ fontSize: 13, color: COLORS.textLight }}>Lädt…</div>}
      {!loading && rows.length === 0 && <Card><div style={{ fontSize: 13, color: COLORS.textLight }}>Keine Salons in dieser Ansicht.</div></Card>}
      {!loading && rows.map(s => {
        const open = openId === s.id;
        const lastLoginDays = daysAgo(s.last_login);
        return (
          <Card key={s.id} style={{ marginBottom: 12, padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{s.name} <span style={{ fontWeight: 400, color: COLORS.textLight, fontSize: 12 }}>#{s.id}{s.city ? ` · ${s.city}` : ""}{s.business_type === "kosmetik" ? " · Kosmetik" : ""}</span></div>
                <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  {trialLabel(s)}
                  {s.online_booking_enabled ? <Badge text="Online-Buchung an" color={COLORS.success} /> : <Badge text="Online-Buchung aus" color={COLORS.textLight} />}
                  {s.pos_enabled && <Badge text="Kasse" color={COLORS.accent} />}
                  {s.seasonal_decor_enabled === false && <Badge text="Saison-Deko aus" color={COLORS.textLight} />} {/* SEASONAL_DECOR_v1 */}
                  {s.trial_reminder_sent_at && <Badge text={`Erinnerung ${fmtDate(s.trial_reminder_sent_at)}`} color={COLORS.info} />}
                </div>
              </div>
              <div style={{ flex: "0 0 180px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span>Einrichtung</span><b style={{ color: progressColor(s.progress_done, s.progress_total) }}>{s.progress_done}/{s.progress_total}</b>
                </div>
                <div style={{ height: 8, background: "var(--border)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${Math.round(100 * s.progress_done / s.progress_total)}%`, height: "100%", background: progressColor(s.progress_done, s.progress_total), transition: "width .3s" }} />
                </div>
              </div>
              <div style={{ flex: "1 1 200px", fontSize: 12, color: COLORS.textLight, lineHeight: 1.6 }}>
                <div>Letzter Login: <b style={{ color: lastLoginDays === null ? COLORS.danger : lastLoginDays > 7 ? COLORS.warning : COLORS.text }}>{s.last_login ? `${fmtDate(s.last_login)} (vor ${lastLoginDays} T.)` : "noch nie"}</b></div>
                <div>Termine: <b style={{ color: COLORS.text }}>{s.counts.appointments}</b> gesamt · <b style={{ color: COLORS.text }}>{s.counts.appointments_7d}</b> neu in 7 T. · <b style={{ color: COLORS.text }}>{s.counts.appointments_future}</b> ab heute</div>
                <div>Nächster Schritt: <b style={{ color: s.next_step ? COLORS.primary : COLORS.success }}>{s.next_step || "alles erledigt"}</b></div>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button onClick={() => setOpenId(open ? null : s.id)} style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "transparent", color: COLORS.text, cursor: "pointer" }}>{open ? "Details ▲" : "Details ▼"}</button>
                <button onClick={() => navigate && navigate("admin-salons")} style={{ padding: "6px 10px", fontSize: 12, borderRadius: 6, border: "none", background: COLORS.primary, color: "#fff", cursor: "pointer" }}>Salon</button>
              </div>
            </div>
            {s.attention && s.attention.length > 0 && (
              <div style={{ marginTop: 10, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>⚠ {s.attention.join(" · ")}</div>
            )}
            {open && (
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 8 }}>
                {s.steps.map(st => (
                  <div key={st.key} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, padding: "8px 10px", borderRadius: 6, background: st.done ? "rgba(16,185,129,0.10)" : "var(--cardHover)" }}>
                    <span style={{ color: st.done ? COLORS.success : COLORS.textLight, fontWeight: 700 }}>{st.done ? "✓" : "○"}</span>
                    <div>
                      <div style={{ fontWeight: 600, color: COLORS.text }}>{st.label}</div>
                      {st.detail && <div style={{ color: COLORS.textLight }}>{st.detail}</div>}
                    </div>
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1", fontSize: 11.5, color: COLORS.textLight, lineHeight: 1.6 }}>
                  Inhaber-Login: {s.owner_email || "—"} · Kunden: {s.counts.customers} · Online-Buchungen: {s.counts.online_bookings} · Öffnungszeiten je Tag: {s.opening_hours_set ? "gepflegt" : "nicht gepflegt"} · 24h-Erinnerung: {s.sms_reminder_24h ? "an" : "aus"}{s.prefer_email ? " (bevorzugt E-Mail)" : ""} · Salon seit {fmtDate(s.created_at)}
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export const AdminUpdatesPage = () => {
  const [flags, setFlags] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showNewFlag, setShowNewFlag] = useState(false);
  const [newFlagName, setNewFlagName] = useState("");
  const [newFlagDesc, setNewFlagDesc] = useState("");
  const [activeTab, setActiveTab] = useState("flags");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  const loadData = async () => {
    const [fRes, sRes] = await Promise.all([api.getFeatureFlags(), api.getAdminSalons()]);
    if (fRes.ok) setFlags(fRes.data);
    if (sRes.ok) setSalons(sRes.data.salons || sRes.data || []);
    setLoading(false);
  };
  useEffect(() => { loadData(); }, []);

  const updateFlag = (name, field, value) => {
    setFlags(prev => prev.map(f => f.name === name ? { ...f, [field]: value } : f));
  };

  const saveAll = async () => {
    setSaving(true);
    const payload = flags.map(f => ({
      name: f.name,
      enabled_global: f.enabled_global ? 1 : 0,
      enabled_plans: f.enabled_plans || [],
      enabled_salons: f.enabled_salons || [],
    }));
    const res = await api.updateFeatureFlags(payload);
    if (res.ok) showToast("Alle Flags gespeichert!");
    setSaving(false);
  };

  const createFlag = async () => {
    if (!newFlagName.trim()) return;
    const res = await api.createFeatureFlag({ name: newFlagName, description: newFlagDesc });
    if (res.ok) {
      showToast("Neues Feature erstellt!");
      setShowNewFlag(false);
      setNewFlagName("");
      setNewFlagDesc("");
      await loadData();
    } else {
      showToast(res.data?.error || "Fehler");
    }
  };

  const deleteFlag = async (name) => {
    if (!confirm(`Feature "${name}" wirklich löschen?`)) return;
    await api.deleteFeatureFlag(name);
    showToast(`"${name}" gelöscht`);
    await loadData();
  };

  // Determine status of a flag
  const getStatus = (flag) => {
    if (flag.enabled_global) return { label: "Live", color: COLORS.success, icon: "✅" };
    const plans = flag.enabled_plans || [];
    const salonIds = flag.enabled_salons || [];
    if (plans.length > 0 && salonIds.length > 0) return { label: "Teilweise + Testing", color: COLORS.accent, icon: "⚡" };
    if (plans.length > 0) return { label: "Teilweise Live", color: COLORS.info, icon: "⚡" };
    if (salonIds.length > 0) return { label: "Testing", color: COLORS.warning, icon: "🔬" };
    return { label: "Deaktiviert", color: COLORS.textLight, icon: "⏸" };
  };

  // Quick action: "Go Live" — enable globally
  const goLive = (name) => {
    setFlags(prev => prev.map(f => f.name === name ? { ...f, enabled_global: 1 } : f));
  };

  // Quick action: Test at specific salon
  const toggleTestSalon = (flagName, salonId) => {
    setFlags(prev => prev.map(f => {
      if (f.name !== flagName) return f;
      const current = f.enabled_salons || [];
      const updated = current.includes(salonId) ? current.filter(id => id !== salonId) : [...current, salonId];
      return { ...f, enabled_salons: updated };
    }));
  };

  const togglePlan = (flagName, plan) => {
    setFlags(prev => prev.map(f => {
      if (f.name !== flagName) return f;
      const current = f.enabled_plans || [];
      const updated = current.includes(plan) ? current.filter(p => p !== plan) : [...current, plan];
      return { ...f, enabled_plans: updated };
    }));
  };

  const PLANS = ["trial", "starter", "pro", "premium"];
  const PLAN_LABELS = { trial: "Trial", starter: "Starter", pro: "Pro", premium: "Premium" };

  if (loading) return <div style={{ textAlign: "center", padding: 40 }}>Lade Feature-Flags...</div>;

  return (
    <div>
      <Header title="Updates & Feature Rollout" subtitle="Features testen, freischalten und verwalten" />

      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, background: COLORS.primary, color: "white", padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
          {toast}
        </div>
      )}

      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `2px solid ${COLORS.border}` }}>
        {[
          { id: "flags", label: "Feature Flags", icon: Zap },
          { id: "changelog", label: "Changelog", icon: Activity },
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", fontSize: 13, fontWeight: isActive ? 700 : 500,
              background: "none", border: "none", cursor: "pointer", color: isActive ? COLORS.primary : COLORS.textLight,
              borderBottom: isActive ? `2px solid ${COLORS.primary}` : "2px solid transparent", marginBottom: -2, transition: "all 0.15s"
            }}>
              <Icon size={15} /> {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "flags" && (
        <div>
          {/* Action bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 13, color: COLORS.textLight }}>
              {flags.length} Features — {flags.filter(f => f.enabled_global).length} Live, {flags.filter(f => !f.enabled_global && (f.enabled_salons || []).length > 0).length} Testing
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="secondary" size="sm" onClick={() => setShowNewFlag(true)}><Plus size={13} /> Neues Feature</Btn>
              <Btn size="sm" onClick={saveAll} disabled={saving}>{saving ? "Speichere..." : "Alle Änderungen speichern"}</Btn>
            </div>
          </div>

          {/* New flag form */}
          {showNewFlag && (
            <Card style={{ marginBottom: 16, border: `2px solid ${COLORS.primary}30` }}>
              <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Neues Feature anlegen</h3>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: 12, alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Name (key)</label>
                  <input value={newFlagName} onChange={e => setNewFlagName(e.target.value)} placeholder="z.B. badges"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 4, display: "block" }}>Beschreibung</label>
                  <input value={newFlagDesc} onChange={e => setNewFlagDesc(e.target.value)} placeholder="Was macht dieses Feature?"
                    style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, boxSizing: "border-box" }} />
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn size="sm" onClick={createFlag}>Anlegen</Btn>
                  <Btn size="sm" variant="secondary" onClick={() => setShowNewFlag(false)}>X</Btn>
                </div>
              </div>
            </Card>
          )}

          {/* Flags list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {flags.map(flag => {
              const status = getStatus(flag);
              const testSalonIds = flag.enabled_salons || [];
              const enabledPlans = flag.enabled_plans || [];
              const testSalonNames = testSalonIds.map(id => salons.find(s => s.id === id)?.name || `#${id}`);

              return (
                <Card key={flag.name} style={{ border: flag.enabled_global ? `1px solid ${COLORS.success}40` : `1px solid ${COLORS.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    {/* Left: Name + Status */}
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 700 }}>{flag.name}</span>
                        <span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${status.color}15`, color: status.color }}>
                          {status.icon} {status.label}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textLight }}>{flag.description || "Keine Beschreibung"}</div>

                      {/* Test salons info */}
                      {testSalonNames.length > 0 && !flag.enabled_global && (
                        <div style={{ marginTop: 6, fontSize: 11, color: COLORS.warning }}>
                          🔬 Testing bei: {testSalonNames.join(", ")}
                        </div>
                      )}
                      {/* Enabled plans info */}
                      {enabledPlans.length > 0 && !flag.enabled_global && (
                        <div style={{ marginTop: 4, fontSize: 11, color: COLORS.info }}>
                          ⚡ Aktiv für: {enabledPlans.map(p => PLAN_LABELS[p] || p).join(", ")}
                        </div>
                      )}
                    </div>

                    {/* Right: Quick actions */}
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                      {!flag.enabled_global && (
                        <Btn size="sm" variant="success" onClick={() => goLive(flag.name)}>
                          <CheckCircle size={13} /> Live schalten
                        </Btn>
                      )}
                      {flag.enabled_global && (
                        <Btn size="sm" variant="secondary" onClick={() => updateFlag(flag.name, "enabled_global", 0)}>
                          Deaktivieren
                        </Btn>
                      )}
                      <button onClick={() => deleteFlag(flag.name)} title="Feature löschen"
                        style={{ background: `${COLORS.danger}10`, border: "none", borderRadius: 6, padding: "6px 8px", cursor: "pointer", color: COLORS.danger }}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Expandable detail: Plans + Salon testing */}
                  {!flag.enabled_global && (
                    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}`, display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
                      {/* Plan toggles */}
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 8, display: "block" }}>Freigabe per Plan</span>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {PLANS.map(plan => {
                            const active = enabledPlans.includes(plan);
                            return (
                              <button key={plan} onClick={() => togglePlan(flag.name, plan)} style={{
                                padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer",
                                border: active ? "none" : `1px solid ${COLORS.border}`,
                                background: active ? COLORS.primary : "var(--card)",
                                color: active ? "white" : COLORS.textLight,
                                transition: "all 0.15s"
                              }}>
                                {PLAN_LABELS[plan]}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Salon test toggles */}
                      <div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.textLight, marginBottom: 8, display: "block" }}>Testen bei Salon</span>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {salons.map(salon => {
                            const active = testSalonIds.includes(salon.id);
                            return (
                              <button key={salon.id} onClick={() => toggleTestSalon(flag.name, salon.id)} style={{
                                padding: "5px 14px", borderRadius: 20, fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer",
                                border: active ? "none" : `1px solid ${COLORS.border}`,
                                background: active ? `${COLORS.warning}` : "var(--card)",
                                color: active ? "white" : COLORS.textLight,
                                transition: "all 0.15s"
                              }}>
                                🔬 {salon.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {activeTab === "changelog" && (
        <div>
          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Aktueller Release</h3>
            <div style={{ padding: 14, background: `${COLORS.success}08`, borderRadius: 8, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Shearly v1.3.0</div>
              <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>Deployed: 13.03.2026 — Alle Salons aktualisiert</div>
            </div>
            <h4 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700 }}>Changelog</h4>
            {[
              { version: "v1.3.0", date: "13.03.2026", changes: "Abzeichen-System (Badges), Schichtplan voll funktional (CRUD + Urlaub), Feature-Rollout-System" },
              { version: "v1.2.0", date: "09.03.2026", changes: "Provisionen & Trinkgeld, Gutscheine, Online-Buchung, Schichtplan" },
              { version: "v1.1.0", date: "15.02.2026", changes: "DATEV-Export, Google Business, erweiterte CRM-Kundenkartei" },
              { version: "v1.0.0", date: "01.01.2026", changes: "Initial Release — Kalender, CRM, KI-Telefonie, Zahlungen" },
            ].map((r, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <strong>{r.version}</strong>
                  <span style={{ color: COLORS.textLight }}>{r.date}</span>
                </div>
                <div style={{ color: COLORS.textLight, marginTop: 4 }}>{r.changes}</div>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
};